import { api, ApiResponseError } from './api';

// Types — unchanged from Supabase schema
export interface Case {
  id: string;
  case_name: string;
  case_ref: string;
  client_firm: string | null;
  status: string;
  s3_bucket: string | null;
  s3_prefix: string | null;
  doc_viewer_base_url: string | null;
  created_at: string;
  /** The matter's court record, added to `cases` by #619 and written through
   *  `set_case_caption()`. Optional because they do not exist on a database
   *  where the expand migration has not run. `docket` + `judge` are what the
   *  prototype's header strip shows ("LASC No. 20STCV02913 · Hon. … , Dept. 3"). */
  caption?: string | null;
  court?: string | null;
  docket?: string | null;
  judge?: string | null;
  /** #1093 — the per-case gate on the new Claims Matrix. NULL (or absent, on a
   *  database where the expand migration has not run) means the old screen,
   *  which is every case until #1102 flips one. Deliberately a column and not a
   *  `VITE_` build flag: turning it back off must not be a rebuild plus a
   *  CloudFront invalidation applied to every matter at once, while the preview
   *  extraction it pairs with is gated to a single named case server-side.
   *  `handle_list_cases` is `SELECT *`, so this arrives without API work. */
  claims_matrix_v2_enabled_at?: string | null;
}

// Run-status state machine (migration 058). Layer 1d foundation — the rest
// of the surface (production-card badge #256, per-request indicator #213,
// stale-on-edit + bulk re-scan #240) wires onto this enum in Layer 4.
//
// States:
//   - not_run   default; no scan has finished against this entity.
//   - running   a scan is currently in flight.
//   - scanned   a scan finished successfully.
//   - stale     a substantive change invalidated a prior scan.
//   - failed    last scan errored out and was not retried.
//
// On `Production`, the value the API returns is plan §Q1 (a), relaxed by
// migration 061 — "≥`app.production_scan_threshold` of eligible docs
// (process_status NOT IN (SKIPPED, DUPLICATE)) have ai_summary" rolls up
// to `scanned`; otherwise `not_run`. The threshold defaults to 0.95 and
// is configurable via the `GENIAC_PRODUCTION_SCAN_THRESHOLD` Lambda env
// var. A future explicit production-scan endpoint (Q1 option (b)) will
// write `running` / `failed` directly to the column and the API will
// surface those values verbatim.
export type RequestRunStatus =
  | 'not_run'
  | 'running'
  | 'scanned'
  | 'stale'
  | 'failed';

export interface Production {
  id: string;
  case_id: string;
  production_ref: string;
  production_name: string;
  direction: 'received' | 'outgoing';
  s3_bucket: string | null;
  s3_prefix: string | null;
  doc_viewer_base_url: string | null;
  sort_order: number;
  created_at: string;
  doc_count: number;
  reviewed_count: number;
  custodians: string[];
  // RAW per-custodian doc count (every doc bearing the custodian). Used for
  // the "M docs" half of the "N threads · M docs" count line and the doc-basis run %.
  custodian_counts: Record<string, number>;
  // FAMILY-UNIT per-custodian count: distinct family units that contain any of
  // the custodian's docs. Equals the row total the (family-aware) custodian
  // filter returns from /documents/search, so the per-custodian card BADGE
  // matches the rows the user actually sees. Differs from custodian_counts
  // whenever a custodian's docs roll up under a cross-custodian thread's
  // terminal rep (e.g. 5 docs → 1 family). Optional on the wire so a frontend
  // ahead of the API tolerates backends without it (falls back to the raw count).
  custodian_family_counts?: Record<string, number>;
  // Per-custodian derived status (migration 061). Each custodian's
  // status is computed against ONLY that custodian's docs, using the
  // same eligibility filter and threshold as the production-wide
  // predicate. The frontend reads this when a production splits into
  // per-custodian cards so the badge matches the displayed `N docs`.
  // Empty `{}` for productions without custodian metadata. Optional on
  // the wire so older API builds (pre-061) don't trip the type.
  custodian_run_statuses?: Record<string, RequestRunStatus>;
  // #605 — per-custodian tile display labels, keyed by canonical_name.
  // Populated from production_custodian_ranges.display_name when set;
  // otherwise the raw custodian canonical_name is used as the tile label.
  // Optional on the wire so a frontend ahead of API 092 degrades gracefully.
  custodian_display_names?: Record<string, string>;
  // Derived per Q1 (a) until an explicit scan-status writer lands. See
  // RequestRunStatus comment above for state semantics.
  latest_run_status: RequestRunStatus;
  latest_run_at: string | null;
  latest_run_id: string | null;
  // Per-case cosmetic rename of the production card (#250). LEFT-JOINed
  // from `collection_display_overrides`. Null when no override has been
  // set; the card render falls back to `production_name`. Optional on
  // the wire so deploys mid-rollout (frontend ahead of API) tolerate
  // backends without migration 059.
  display_name_override?: string | null;
  // #545 — lifecycle axis (migration 085). `collection` = our pre-production
  // working set; `formal_production` = Bates-stamped set served between
  // parties. Optional on the wire so a frontend ahead of API 085 tolerates
  // backends without the column (sections degrade to the flat grid).
  kind?: 'collection' | 'formal_production';
  // #621 — production-level Bates span (MIN begin / MAX end over the
  // production's docs), derived server-side from the productions-list
  // LATERAL scan. Null on empty productions. Optional on the wire so a
  // frontend ahead of the API (or vice versa) degrades to "no Bates
  // line" rather than crashing — mirrors docTotal / custodian_run_statuses.
  bates_begin?: string | null;
  bates_end?: string | null;
  // #545 — formal production/served date (the tile label + chronological sort
  // key). Null on collections and on formal_productions not yet confirmed.
  produced_on?: string | null;
  producing_party_id?: string | null;
  // #545 — the producing party (NULL on collections / unclassified rows). Tile
  // SECTIONS (Plaintiffs'/Defendants'/Third-Party) are derived on the client
  // from `roles`. Roles mirrors case_parties.roles (e.g. ['plaintiff'],
  // ['defendant','cross_complainant']).
  producing_party?: ProducingParty | null;
}

export interface ProducingParty {
  id: string;
  canonical_name: string;
  short_name: string | null;
  roles: string[];
}

// A "virtual collection" is a (production, custodian?) pair used by the UI.
// For productions with a single custodian or no custodian metadata, custodian
// is null and no custodian_filter is applied to document queries.
// For productions with 2+ custodians, each custodian becomes its own entry
// so the reviewer can browse one person's documents at a time.
//
// Run-status fields (`latestRunStatus`, `latestRunAt`, `latestRunId`) mirror
// the per-production columns from migration 058, surfaced through
// `GET /cases/{id}/productions`. Cards split by custodian under the same
// production all carry the same status — the production is the scan unit
// (#256, Layer 4a). `latestRunId` is carried for future drill-down (e.g. a
// "view scan log" affordance) and is intentionally not rendered today.
export interface VirtualCollection {
  productionId: string;
  custodian: string | null;
  name: string;
  // Default name as rendered before any override applies. When the user
  // clears their custom name (DELETE → fallback), the card render rolls
  // back to this. Same value as `name` when no override is active.
  defaultName: string;
  // Per-case rename of the underlying production card (#250). Null when
  // no override has been set. Custodian-split entries inherit `null`
  // because the rename surface is keyed on the production card itself,
  // not on the per-custodian splits beneath it.
  displayNameOverride: string | null;
  // The badge N. For custodian-split cards this is the FAMILY-UNIT count
  // (== rows the family-aware custodian filter returns), so the badge matches
  // what the user sees on click. For whole-production cards it's the doc count.
  docCount: number;
  // RAW doc total behind a custodian split (every doc bearing the custodian),
  // shown as the "M docs" half of the "N threads · M docs" count line. Only set
  // on custodian splits; when absent or == docCount the line collapses to plain
  // "N docs" (no thread/family collapse to explain). Optional so other
  // VirtualCollection constructors (batch splits, tests) need no change.
  docTotal?: number;
  reviewedCount: number;
  latestRunStatus: RequestRunStatus;
  latestRunAt: string | null;
  latestRunId: string | null;
  // #545 — carried so the Productions screen can group cards into party
  // sections and label/sort them by production date. Custodian splits inherit
  // their parent production's party + date. Null on collections / unclassified
  // / pre-085 backends.
  producedOn: string | null;
  producingParty: ProducingParty | null;
  // #545 — the lifecycle axis, carried so the UI can tell "a formal
  // production nobody has classified yet" (needsClassification, shows a
  // to-do hint) apart from "a collection, which is never classified by
  // design" (producingParty is null in both cases, kind disambiguates).
  // Null on the legacy batch-card path / pre-085 backends.
  kind: 'collection' | 'formal_production' | null;
  // #636 — custodian-first collections. Present ONLY on tiles built by
  // `toCustodianCollections` from the person-rollup endpoint. On a
  // `kind:"custodian"` rollup entry this carries that person's collection
  // sets (bands) so the in-table collection-set dropdown can offer them;
  // on a whole-production (STIIIZY) tile it is an empty array. Undefined on
  // the legacy `toVirtualCollections` path.
  collectionSets?: CollectionSet[];
  // #636, D5 — the custodian's `case_parties.id`, threaded onto a custodian
  // tile so an upload from that view defaults `responding_party_id` to the
  // person. Null when the rollup could not resolve a party; undefined on the
  // legacy path and on whole-production tiles.
  partyId?: string | null;
  // #621 — production-level Bates span, surfaced on the card as a "BATES
  // RANGE" line. Set to value-or-null by toVirtualCollections: the raw
  // begin/end on a whole-production card, and ALWAYS null on a custodian
  // split (custodian sub-cards never show a Bates span). Optional so the
  // legacy batch-card path and other VirtualCollection constructors
  // (tests, RequestsBoardView) need no change and degrade to "no Bates
  // line". The card render additionally gates on kind === 'formal_production'
  // && custodian === null, so collections never render it.
  batesBegin?: string | null;
  batesEnd?: string | null;
  // Always-shown core metadata (#406). The underlying production's
  // ingest date and reference, surfaced on the card so reviewers have a
  // stable chronological/ID anchor without drilling in. Carried at the
  // production granularity — custodian-split sub-cards inherit the parent
  // production's values. Null only on the legacy batch-card path, which
  // has no backing `Production` row (the date is sorted on separately
  // there); the card render drops the line when null.
  createdAt: string | null;
  productionRef: string | null;
}

export function toVirtualCollections(
  productions: Production[],
  { splitByCustodian = true }: { splitByCustodian?: boolean } = {},
): VirtualCollection[] {
  const result: VirtualCollection[] = [];
  for (const p of productions) {
    // Same status payload regardless of custodian-splitting — the
    // run-status state machine lives at the production granularity.
    const runStatus = {
      latestRunStatus: p.latest_run_status,
      latestRunAt: p.latest_run_at,
      latestRunId: p.latest_run_id,
    };
    // #545: party + production date ride at the production granularity, so
    // custodian splits under one production all carry the same values.
    const partyDate = {
      producedOn: p.produced_on ?? null,
      producingParty: p.producing_party ?? null,
      kind: p.kind ?? null,
    };
    if (splitByCustodian && (p.custodians ?? []).length > 1) {
      // Custodian splits don't carry the override — the override applies
      // to the production card, not the per-custodian sub-card. The
      // partner-dashboard rename surface is the production-level one.
      for (const custodian of p.custodians) {
        // Per-custodian status (migration 061): the LATERAL in the
        // productions list query computes this against only this
        // custodian's docs so the badge's PCT matches the card's N.
        // Falls back to the production-wide status when an older API
        // build doesn't carry the per-custodian map.
        const custodianStatus =
          p.custodian_run_statuses?.[custodian] ?? p.latest_run_status;
        const rawDocs = p.custodian_counts[custodian] ?? 0;
        // Badge = family units (matches the family-aware filter's row total).
        // Fall back to the raw doc count if an older API build doesn't carry
        // the family-unit map, so the badge degrades to the pre-fix number
        // rather than 0.
        const familyUnits = p.custodian_family_counts?.[custodian] ?? rawDocs;
        result.push({
          productionId: p.id,
          custodian,
          name: p.custodian_display_names?.[custodian] ?? custodian,
          defaultName: custodian,
          displayNameOverride: null,
          docCount: familyUnits,
          docTotal: rawDocs,
          // #621 — custodian splits never show a Bates span (they carry
          // Elluma/control IDs, not Bates; and a split is a sub-slice of a
          // production, not a Bates-contiguous set).
          batesBegin: null,
          batesEnd: null,
          reviewedCount: 0,
          latestRunStatus: custodianStatus,
          latestRunAt: p.latest_run_at,
          latestRunId: p.latest_run_id,
          ...partyDate,
          // Core metadata (#406) — production-granular, inherited by each
          // custodian split.
          createdAt: p.created_at,
          productionRef: p.production_ref,
        });
      }
    } else {
      const override = p.display_name_override ?? null;
      result.push({
        productionId: p.id,
        custodian: null,
        name: override ?? p.production_name,
        defaultName: p.production_name,
        displayNameOverride: override,
        docCount: p.doc_count,
        docTotal: p.doc_count,
        // #621 — whole-production Bates span (value-or-null).
        batesBegin: p.bates_begin ?? null,
        batesEnd: p.bates_end ?? null,
        reviewedCount: p.reviewed_count,
        // Core metadata (#406).
        createdAt: p.created_at,
        productionRef: p.production_ref,
        ...runStatus,
        ...partyDate,
      });
    }
  }
  return result;
}

// ─── Custodian-first collections (#636, A3) ───────────────────────────────
//
// The `GET /cases/{id}/collections/custodians` rollup is a discriminated
// list: one `kind:"custodian"` entry per person (aggregated across
// productions, carrying that person's collection sets / bands), plus a
// `kind:"production"` fallback for each range-less production (no
// `production_custodian_ranges` bands — e.g. STIIIZY). See feature doc §A3.

// One collection set == one `production_custodian_ranges` band (mig 088/091):
// a single Bates span, for one custodian, in one production, labeled by
// `display_name`. The band `collection_set_id` is the ONLY filter value —
// `label`/`display_name` is a display string, NEVER a filter value (D3, HC6).
export interface CollectionSet {
  collection_set_id: string;
  label: string;
  production_id: string;
  bates_begin: string;
  bates_end: string;
  doc_count: number;
  family_count: number;
}

export interface CustodianRollupCustodian {
  kind: 'custodian';
  custodian: string;
  party_id: string | null;
  doc_count: number;
  family_count: number;
  collection_sets: CollectionSet[];
}

export interface CustodianRollupProduction {
  kind: 'production';
  production_id: string;
  name: string;
  doc_count: number;
  family_count: number;
  collection_sets: CollectionSet[];
}

export type CustodianRollupEntry =
  | CustodianRollupCustodian
  | CustodianRollupProduction;

export async function fetchCollectionCustodians(
  caseId: string,
): Promise<CustodianRollupEntry[]> {
  return api.get<CustodianRollupEntry[]>(
    `/cases/${caseId}/collections/custodians`,
  );
}

// Map the A3 rollup into VirtualCollection-shaped tiles for
// `<CollectionsDashboard>`.
//   kind:"custodian"  → productionId='all' (the existing View-all sentinel)
//                       + custodian=canonicalName, so clicking reuses the
//                       `?collection=all&custodian=…` sentinel path. Each
//                       person appears ONCE (the endpoint already rolls up
//                       across productions — HC1). The tile carries the
//                       person's `collectionSets` (for the band dropdown) and
//                       `partyId` (for the D5 upload binding).
//   kind:"production" → the existing whole-production tile (custodian=null),
//                       collectionSets=[] (range-less; STIIIZY — HC5).
// The docCount badge is the FAMILY-UNIT count (matches the family-aware
// filter's row total, same convention as the custodian splits in
// `toVirtualCollections`); docTotal is the raw doc count so the card's
// "N threads · M docs" line stays honest.
export function toCustodianCollections(
  rollup: CustodianRollupEntry[],
): VirtualCollection[] {
  return rollup.map((entry) => {
    if (entry.kind === 'custodian') {
      return {
        productionId: 'all',
        custodian: entry.custodian,
        name: entry.custodian,
        defaultName: entry.custodian,
        displayNameOverride: null,
        docCount: entry.family_count,
        docTotal: entry.doc_count,
        reviewedCount: 0,
        // A3 does not carry a per-custodian run status; the run-status
        // surface is A8's state-aware empty state, not the tile badge.
        latestRunStatus: 'not_run' as RequestRunStatus,
        latestRunAt: null,
        latestRunId: null,
        producedOn: null,
        producingParty: null,
        kind: null,
        createdAt: null,
        productionRef: null,
        collectionSets: entry.collection_sets,
        partyId: entry.party_id,
      };
    }
    return {
      productionId: entry.production_id,
      custodian: null,
      name: entry.name,
      defaultName: entry.name,
      displayNameOverride: null,
      docCount: entry.family_count,
      docTotal: entry.doc_count,
      reviewedCount: 0,
      latestRunStatus: 'not_run' as RequestRunStatus,
      latestRunAt: null,
      latestRunId: null,
      producedOn: null,
      producingParty: null,
      kind: null,
      createdAt: null,
      productionRef: null,
      collectionSets: [],
      partyId: null,
    };
  });
}

export interface Document {
  id: string;
  case_id: string;
  production_id: string | null;
  bates_begin: string;
  bates_end: string | null;
  begin_attachment: string | null;
  end_attachment: string | null;
  family_position: string | null;
  original_filename: string | null;
  subject: string | null;
  time_sent: string | null;
  to_addr: string | null;
  from_addr: string | null;
  cc_addr: string | null;
  bcc_addr: string | null;
  file_extension: string | null;
  description: string | null;
  file_size: number | null;
  ai_doc_type: string | null;
  ai_doc_date: string | null;
  short_summary: string | null;
  brief_summary: string | null;
  ai_summary: string | null;
  /** Bounded (200-char) slice of `ai_summary`, supplied by the list endpoints
   *  ONLY for rows with no `brief_summary` and no `short_summary` — those would
   *  otherwise render an empty summary cell, since the list does not ship
   *  `ai_summary` (#869). NULL for every other row, which is what preserves the
   *  payload win: see `SUMMARY_PREVIEW_COLUMN` in api/routes/documents.py.
   *
   *  NOT a short summary, and not a hydrated long one — a preview. Kept out of
   *  `short_summary` deliberately so nothing that ships that field (the Excel
   *  export, under "Summary") can emit a mid-sentence fragment. */
  summary_preview?: string | null;
  combined_date: string | null;
  page_count: number | null;
  doc_type: string | null;
  has_summary: boolean;
  process_status: string | null;
  reuse_from: string | null;
  dupe_group: string | null;
  content_hash: string | null;
  s3_doc_key: string | null;
  s3_text_key: string | null;
  doc_viewer_url: string | null;
  inferred_thread_id: string | null;
  inferred_thread_position: string | null;
  production_batch: string | null;
}

export interface DocReview {
  id: string;
  document_id: string;
  case_id: string;
  reviewed: boolean;
  reviewed_by_email: string | null;
  reviewed_at: string | null;
  classification: 'hot' | 'critical' | 'tbd' | null;
  classified_by_email: string | null;
  classified_at: string | null;
  privileged: boolean;
  privileged_by_email: string | null;
  privileged_at: string | null;
}

export interface CaseIssue {
  id: string;
  case_id: string;
  label: string;
  color: string;
  sort_order: number;
  description: string | null;
}

export interface DocComment {
  id: string;
  document_id: string;
  case_id: string;
  author_email: string;
  author_name: string | null;
  content: string;
  created_at: string;
}

export interface AuditLogEntry {
  id: string;
  case_id: string;
  document_id: string | null;
  user_email: string;
  action: string;
  entity_type: string;
  old_value: any;
  new_value: any;
  created_at: string;
}

export interface CaseMember {
  id: string;
  email: string;
  case_id: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

// --- Data functions (server-paginated search is in the section below) ---

export async function fetchUserCases(): Promise<Case[]> {
  return api.get<Case[]>('/cases');
}

export async function fetchProductions(
  caseId: string,
  // Pass `{ custodians: true }` ONLY when the result will be fed to
  // `toVirtualCollections` with `splitByCustodian` on — the
  // per-custodian aggregate is the most expensive part of the server
  // query (a second per-production scan of `documents`), so the server
  // skips it unless asked. When omitted, the `custodians` /
  // `custodian_counts` / `custodian_run_statuses` fields come back as
  // empty `[]` / `{}`, which `toVirtualCollections` already tolerates
  // (no split → one card per production). The request and the split
  // intent MUST agree, or productions silently stop splitting.
  // `direction` filters the returned productions to one per-production
  // direction ('received' | 'outgoing') server-side (omit for all). Document
  // Discovery's outgoing toggle passes 'outgoing'. `producedOnly` scopes the
  // per-card doc/review/custodian COUNTS to documents disclosed to the
  // opposing side (production_status='produced') and drops cards with zero
  // produced docs — mirrors is_disclosed()'s produced arm. Received cards
  // never set it (received docs are all produced anyway; the received path
  // filters client-side and leaves counts whole).
  {
    custodians = false,
    direction,
    producedOnly = false,
  }: {
    custodians?: boolean;
    direction?: 'received' | 'outgoing';
    producedOnly?: boolean;
  } = {},
): Promise<Production[]> {
  const parts: string[] = [];
  if (custodians) parts.push('custodians=1');
  if (direction) parts.push(`direction=${encodeURIComponent(direction)}`);
  if (producedOnly) parts.push('produced_only=1');
  const qs = parts.length ? `?${parts.join('&')}` : '';
  return api.get<Production[]>(`/cases/${caseId}/productions${qs}`);
}

/** #545 PR-B — set the producing party and/or production date on a formal
 * production (admin-gated server-side; a non-admin gets a 403 the caller
 * should surface, not swallow). Pass `null` for either field to clear it.
 * Rejects (400, server-side) on a `collection`-kind production. */
export async function updateProductionClassification(
  caseId: string,
  productionId: string,
  updates: { producing_party_id?: string | null; produced_on?: string | null },
): Promise<Production> {
  return api.patch<Production>(
    `/cases/${caseId}/productions/${productionId}`,
    updates,
  );
}

// ─── Collection display-name overlay (#250) ───────────────────────────
//
// Cosmetic rename of production cards (DD) and request-set cards (IR).
// Per-case override; backend always honors a stored override regardless
// of feature-flag state — the flag only gates whether the rename UI is
// visible. See plan §"Layer 2 — Display-name overlay (#250)".

export type CollectionKind = 'discovery_production' | 'incoming_request_set';

/** Maximum length of a stored display-name override. Mirrors the CHECK
 * constraint in migration 058 + the validation in
 * `api/routes/collections.py`. Surfaced here so the rename modal can
 * gate the input client-side without a round-trip. */
export const MAX_COLLECTION_DISPLAY_NAME_LEN = 80;

export interface CollectionDisplayNameOverride {
  case_id: string;
  collection_kind: CollectionKind;
  collection_key: string;
  display_name: string;
  updated_by_email: string;
  updated_at: string;
}

/** Upsert the per-case display-name override for a collection card.
 * `displayName` must be 1..80 chars after trim; the server re-validates. */
export async function putCollectionDisplayName(
  caseId: string,
  kind: CollectionKind,
  key: string,
  displayName: string,
): Promise<CollectionDisplayNameOverride> {
  return api.put<CollectionDisplayNameOverride>(
    `/cases/${caseId}/collections/${kind}/${encodeURIComponent(key)}/display-name`,
    { display_name: displayName },
  );
}

/** Roll back to the default name (Q2: DELETE → no empty-string state). */
export async function deleteCollectionDisplayName(
  caseId: string,
  kind: CollectionKind,
  key: string,
): Promise<{ ok: true; deleted: boolean }> {
  return api.delete<{ ok: true; deleted: boolean }>(
    `/cases/${caseId}/collections/${kind}/${encodeURIComponent(key)}/display-name`,
  );
}

export async function upsertDocReview(review: Partial<DocReview> & { document_id: string; case_id: string }): Promise<DocReview> {
  return api.put<DocReview>('/reviews', review);
}

export async function fetchCaseIssues(caseId: string): Promise<CaseIssue[]> {
  return api.get<CaseIssue[]>(`/cases/${caseId}/issues`);
}

/**
 * @deprecated Use {@link fetchDocumentFacets} (Document Discovery) or
 * {@link fetchIncomingRequestFacets} (Incoming Requests). The new
 * endpoints accept the full filter body so sidebar issue counts move
 * with every active filter, not just production/request-item scope.
 * Also family-expand counts via family_map (this endpoint joined
 * doc_issue_assignments → documents flat, missing families where only
 * an attachment carried the issue tag). Kept for one release of
 * overlap.
 */
export async function fetchIssueCounts(
  caseId: string,
  scope?: { productionId?: string | null; requestItemId?: string | null; setId?: string | null },
): Promise<{ issue_id: string; count: number }[]> {
  const qs = new URLSearchParams();
  if (scope?.productionId) qs.set('production_id', scope.productionId);
  if (scope?.requestItemId) qs.set('request_item_id', scope.requestItemId);
  if (scope?.setId) qs.set('set_id', scope.setId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get<{ issue_id: string; count: number }[]>(`/cases/${caseId}/issues/counts${suffix}`);
}

// ─── Filter-aware facet counts ─────────────────────────────────────────
//
// Both endpoints return per-bucket counts that strip the named facet's
// own predicate from the WHERE before counting — standard "AND across
// facets, OR within facet" rule. Counts equal what the table will show
// when the user clicks each bucket, by construction.

// Mirrors FACET_SPECS in api/routes/_facets_spec.py — a name the server does
// not know is rejected with a 400, so the two lists must stay in step.
export type FacetName = 'issue' | 'comment' | 'doc_type';

export interface FacetCount {
  key: string;
  count: number;
}

export type FacetCountsResponse = Partial<Record<FacetName, FacetCount[]>> & {
  /**
   * Set when the facet query hit the server's search cap (#868). The buckets
   * are then EMPTY BECAUSE UNKNOWN, not because nothing matched — a consumer
   * that renders them as zero states "no documents match this issue" when the
   * truth is "we could not find out", which is the confusion #869 exists to
   * remove. Render counts as unavailable instead.
   */
  search_timed_out?: boolean;
};

export async function fetchDocumentFacets(
  caseId: string,
  searchParams: DocumentSearchParams,
  facets: readonly FacetName[] = ['issue'],
  signal?: AbortSignal,
): Promise<FacetCountsResponse> {
  return api.post<FacetCountsResponse>(
    `/cases/${caseId}/documents/facets`,
    { ...searchParams, facets },
    { signal },
  );
}

// IR facets body shape mirrors the snake_case keys `_match_list_where`
// reads from query strings. The backend's `_normalize_ir_body_to_qs`
// accepts both raw lists (CSV-encoded server-side) and pre-CSV'd
// strings, so callers can pass whichever shape they have on hand.
export type IncomingRequestFacetsBody = Record<
  string,
  string | string[] | number | boolean | undefined
>;

export async function fetchIncomingRequestFacets(
  caseId: string,
  setId: string,
  filters: IncomingRequestFacetsBody,
  facets: readonly FacetName[] = ['issue'],
  signal?: AbortSignal,
): Promise<FacetCountsResponse> {
  return api.post<FacetCountsResponse>(
    `/cases/${caseId}/incoming-requests/sets/${setId}/facets`,
    { ...filters, facets },
    { signal },
  );
}

export async function createCaseIssue(caseId: string, label: string, color: string, description?: string): Promise<CaseIssue> {
  return api.post<CaseIssue>('/issues', { case_id: caseId, label, color, description });
}

export async function updateCaseIssue(issueId: string, fields: { label?: string; color?: string; description?: string }): Promise<CaseIssue> {
  return api.patch<CaseIssue>(`/issues/${issueId}`, fields);
}

export async function deleteCaseIssue(issueId: string): Promise<void> {
  await api.delete(`/issues/${issueId}`);
}

export async function fetchDocIssueAssignments(caseId: string): Promise<{ document_id: string; issue_id: string }[]> {
  return api.get<{ document_id: string; issue_id: string }[]>(`/cases/${caseId}/assignments`);
}

export async function toggleDocIssue(documentId: string, issueId: string, assignedByEmail: string): Promise<boolean> {
  const result = await api.post<{ assigned: boolean }>('/assignments/toggle', {
    document_id: documentId,
    issue_id: issueId,
    assigned_by_email: assignedByEmail,
  });
  return result.assigned;
}

// One (document, issue) row a clear changed — the row-exact undo unit.
export interface ClearedIssueRow {
  document_id: string;
  issue_id: string;
}

// Bulk "clear all" — turn OFF every active issue tag on a document at once (the
// reviewer judges it relevant to no issue). Returns the clicked-doc's active
// issue_ids (for the "Cleared N" label), the exact (doc, issue) rows changed
// across the dupe group (`cleared_rows`, for the optimistic UI re-add only), and
// an opaque `undo_token` — a server-signed, reviewer-bound capability over those
// exact rows. The undo MUST round-trip this token; the row set is authenticated
// server-side from it, never re-derived from client input. Attribution is server-side.
export async function clearDocIssues(
  documentId: string,
): Promise<{ cleared_issue_ids: string[]; cleared_rows: ClearedIssueRow[]; undo_token: string }> {
  return api.post<{ cleared_issue_ids: string[]; cleared_rows: ClearedIssueRow[]; undo_token: string }>(
    '/assignments/clear',
    { document_id: documentId },
  );
}

// Row-exact, atomic undo of a clear: restores precisely the rows the clear changed
// (no dupe-group cascade), so a sibling that never had an issue never gains one.
// Takes the `undo_token` the clear returned — the server reads the row set from the
// token (not from any client-supplied list), so a forged set can't split dupe state.
export async function undoClearDocIssues(undoToken: string): Promise<number> {
  const result = await api.post<{ restored: number }>('/assignments/clear/undo', { undo_token: undoToken });
  return result.restored;
}

export async function fetchDocComments(caseId: string, productionId?: string): Promise<DocComment[]> {
  const query = productionId
    ? `?${new URLSearchParams({ production_id: productionId }).toString()}`
    : '';
  return api.get<DocComment[]>(`/cases/${caseId}/comments${query}`);
}

export async function postComment(comment: { document_id: string; case_id: string; author_email: string; author_name: string; content: string }): Promise<DocComment> {
  return api.post<DocComment>('/comments', comment);
}

export async function fetchDocumentDetail(documentId: string): Promise<{ ai_summary: string | null }> {
  return api.get<{ ai_summary: string | null }>(`/documents/${documentId}/detail`);
}

export async function fetchDocAuditLog(documentId: string): Promise<AuditLogEntry[]> {
  return api.get<AuditLogEntry[]>(`/documents/${documentId}/audit`);
}

export async function fetchCaseAuditLog(caseId: string, limit = 100): Promise<AuditLogEntry[]> {
  return api.get<AuditLogEntry[]>(`/cases/${caseId}/audit`);
}

export async function fetchMyRole(caseRef: string): Promise<string | null> {
  return api.get<string | null>(`/cases/${caseRef}/my-role`);
}

export async function fetchCaseMembers(caseRef: string): Promise<CaseMember[]> {
  return api.get<CaseMember[]>(`/cases/${caseRef}/members`);
}

export async function addCaseMember(caseRef: string, email: string, role: string): Promise<CaseMember> {
  return api.post<CaseMember>('/members', { case_id: caseRef, email, role });
}

export async function updateCaseMemberRole(id: string, role: string): Promise<void> {
  await api.put(`/members/${id}`, { role });
}

export async function removeCaseMember(id: string): Promise<void> {
  await api.delete(`/members/${id}`);
}

export async function createDocAccessCode(): Promise<string | null> {
  try {
    return await api.post<string>('/access-codes');
  } catch (err) {
    console.error('Failed to create access code:', err);
    return null;
  }
}

// Append access code to a document viewer URL (pure function, no API call)
export function appendAccessCode(docViewerUrl: string, code: string | null): string {
  if (!docViewerUrl || docViewerUrl === '#') return '#';
  if (code) {
    const sep = docViewerUrl.includes('?') ? '&' : '?';
    return `${docViewerUrl}${sep}code=${encodeURIComponent(code)}`;
  }
  return docViewerUrl;
}


// ── Server-side paginated document search ────────────────────────────

export interface DocumentSearchParams {
  production_id?: string;
  direction?: 'received' | 'outgoing';
  page?: number;
  page_size?: number;
  sort_column?: string;
  sort_direction?: 'asc' | 'desc';
  search?: string;
  type_filters?: string[];
  thread_filter?: 'all' | 'standalone' | 'in_thread' | 'terminal_only';
  participant_search?: string;
  date_from?: string;
  date_to?: string;
  hide_duplicates?: boolean;
  review_status_filters?: string[];
  importance_filters?: string[];
  reviewed_by?: string;
  privileged_filter?: 'all' | 'privileged' | 'not-privileged';
  issue_filter?: string;
  comment_filter?: 'all' | 'with-comments';
  batch_filter?: string;
  custodian_filter?: string;
  // Responsiveness set-lens (docs/responsiveness-set-lens-feature.md): a doc's
  // FINAL EFFECTIVE state (AI-suggested ∪ human-confirmed, human wins), family-
  // aware. `responsiveness_set_id` scopes it to one request set (the "Entire
  // Request Set" view); absent ⇒ case-wide. Rides WITH responsiveness_filter.
  // 'uncoded' = all − coded (no verdict yet), applied as a negated family-EXISTS.
  responsiveness_filter?: 'responsive' | 'unresponsive' | 'uncoded';
  responsiveness_set_id?: string;
  // Per-RFP scope (unified model): narrows the lens to ONE request ("as though
  // the set held only that RFP"). Emitted by collectionResponsivenessParams;
  // the backend prefers it over responsiveness_set_id when both are present.
  responsiveness_request_item_id?: string;
  // #636 — collection-set (band) scope. The value is ALWAYS the band
  // `collection_set_id` uuid, NEVER the band `display_name`/label (D3, HC6).
  // Family-aware by default; see `band_segregation`.
  collection_set_id?: string;
  // #636, D2b — band segregation mode. Absent / "family" ⇒ family-aware
  // (a cross-band thread surfaces under both bands). "strict" ⇒ flat
  // per-parent (only that band's own docs). Sent only when a band is scoped
  // and the `strictBandSegregation` flag is on.
  band_segregation?: 'family' | 'strict';
  // #636, finding 6 (A4 edit 6) — OUR-side production scope. When true the
  // backend resolves productions by `direction = ANY(OUR_SIDE_DIRECTIONS)`
  // instead of a single `direction`. Used by the custodian-first table so
  // tile == table == matcher scope (a non-`outgoing` OUR-side custodian's
  // docs aren't dropped on click). Mutually exclusive with `direction`.
  our_side?: boolean;
  // Produced-only scope (Document Discovery outgoing toggle). When true the
  // backend restricts the result to documents disclosed to the opposing side
  // (production_status='produced'), excluding the still-under-review /
  // withheld_privileged / clawed_back working set — is_disclosed()'s produced
  // arm. A scope constraint, not a user facet.
  produced_only?: boolean;
  // Exact-match parent-id filter (no family expansion). Used by the Incoming
  // Requests module to hydrate doc-meta for a specific list of match targets.
  ids?: string[];
  // Opt-in: include the full `ai_summary` column in each row. Off by default
  // because the DocumentsScreen paginates a 141K-doc corpus and the column
  // can be multi-KB per row. Set true for bounded id-list fetches where the
  // summary is about to be shown (Incoming Requests bulk enrichment) so the
  // drawer doesn't have to do a per-doc follow-up request.
  include_ai_summary?: boolean;
}

export interface EmbeddedReview {
  reviewed: boolean;
  reviewed_by_email: string | null;
  classification: string | null;
  privileged: boolean;
  privileged_by_email: string | null;
  // doc_reviews.doc_note — the document-level reviewer note. The Incoming
  // Requests drawer writes it; without it here the Review module could not
  // display a note its sibling module had already saved, so the same row
  // rendered differently depending on which surface you opened it from.
  // Distinct from the doc_comments thread, which is a separate table.
  doc_note: string | null;
}

export interface DocumentRow extends Document {
  parent_family_key: string;
  child_count: number;
  review: EmbeddedReview | null;
  issue_ids: string[];
  comment_count: number;
  // IDs of children satisfying the active filters. Navigation only: issue_ids
  // and comment_count above still describe this document alone.
  issue_match_child_ids?: string[];
  comment_match_child_ids?: string[];
}

export interface DocumentSearchResponse {
  rows: DocumentRow[];
  total: number;
  page: number;
  page_size: number;
  // Set by the backend when the search query hit its statement_timeout and was
  // cancelled (a graceful 200 with empty rows, not a 500). The UI surfaces a
  // "search too broad — narrow the scope" banner. Absent on normal responses.
  search_timed_out?: boolean;
}

export interface DocumentMeta {
  doc_types: string[];
  reviewers: string[];
  batches: string[];
  // Per-batch document counts. Keyed by production_batch string. Optional
  // because older API deploys may not return this field — callers should
  // fall back to 0 / hide-on-zero.
  batch_counts?: Record<string, number>;
  // Per-batch latest combined_date (ISO yyyy-mm-dd). Used for chronological
  // sorting on the Document Discovery cards landing. Optional for the same
  // backward-compat reason as batch_counts.
  batch_dates?: Record<string, string | null>;
  resolved_custodians: string[];
  total_all: number;
  total_all_docs: number;
}

export async function fetchDocumentsPage(
  caseId: string,
  params: DocumentSearchParams,
  signal?: AbortSignal,
): Promise<DocumentSearchResponse> {
  return api.post<DocumentSearchResponse>(
    `/cases/${caseId}/documents/search`, params, { signal });
}

export async function fetchDocumentMeta(
  caseId: string,
  scope: {
    productionId?: string;
    direction?: 'received' | 'outgoing';
    // Produced-only scope (Document Discovery outgoing toggle) — keeps the
    // dropdown values + totals aligned with a produced-scoped table.
    producedOnly?: boolean;
  },
): Promise<DocumentMeta> {
  const base = scope.productionId
    ? `production_id=${encodeURIComponent(scope.productionId)}`
    : `direction=${encodeURIComponent(scope.direction!)}`;
  const qs = scope.producedOnly ? `${base}&produced_only=1` : base;
  return api.get<DocumentMeta>(`/cases/${caseId}/documents/meta?${qs}`);
}

// Collection needs only type options, with the same fallback as its rows.
export async function fetchCollectionDocumentTypes(
  caseId: string,
  scope: { productionId?: string; direction?: 'outgoing' | 'our_side' },
): Promise<string[]> {
  const query = new URLSearchParams({ view: 'collection_types' });
  if (scope.productionId) query.set('production_id', scope.productionId);
  else query.set('direction', scope.direction ?? 'outgoing');
  const result = await api.get<{ doc_types: string[] }>(
    `/cases/${caseId}/documents/meta?${query}`,
  );
  return result.doc_types;
}

export async function fetchDocumentChildren(
  caseId: string,
  parentFamilyKey: string,
  productionId: string,
): Promise<DocumentRow[]> {
  const qs = new URLSearchParams({ parent_family_key: parentFamilyKey, production_id: productionId });
  return api.get<DocumentRow[]>(`/cases/${caseId}/documents/children?${qs}`);
}

// Full single-document fetch for drawer deep-link hydration. Returns the same
// DocumentRow shape as items in /documents/search so the drawer can hydrate
// without contortions.
export async function fetchDocumentById(
  documentId: string,
  opts: { producedOnly?: boolean } = {},
): Promise<DocumentRow> {
  // producedOnly scopes the drawer hydration to the SAME produced boundary as
  // the outgoing table/counts, so a `?docId=<withheld/not_produced>` deep-link
  // can't render a non-produced doc inside the produced-only outgoing view
  // (a non-produced id 404s under this scope).
  const qs = opts.producedOnly ? '?produced_only=1' : '';
  return api.get<DocumentRow>(`/documents/${documentId}${qs}`);
}

export interface FilteredDocIdsResponse {
  ids: string[];
  total: number;
  truncated: boolean;
}

export async function fetchFilteredDocIds(
  caseId: string,
  params: DocumentSearchParams,
): Promise<FilteredDocIdsResponse> {
  return api.post<FilteredDocIdsResponse>(`/cases/${caseId}/documents/search/ids`, params);
}

// ── "Start Review" focused-mode ordered queue (issue #512) ───────────
// Ordered PARENT-row id snapshot for the filtered cohort the table shows.
// Same JSON filter body as /documents/search (all filters + sort_column +
// sort_direction). Unlike /search/ids this is parent-only and in sort order
// — focused mode marches the parent queue, so the snapshot must match the
// table exactly. Capped server-side (10K): when the cohort exceeds the cap,
// `truncated` is true and `ids` is empty (Start Review is refused — no
// partial review).
export interface ReviewQueueResponse {
  ids: string[];
  total: number;
  truncated: boolean;
}

export async function fetchReviewQueue(
  caseId: string,
  params: DocumentSearchParams,
): Promise<ReviewQueueResponse> {
  return api.post<ReviewQueueResponse>(`/cases/${caseId}/documents/search/queue`, params);
}

export interface SearchCountsResponse {
  /** Parent/family count — what the review table's row count shows. */
  total_families: number;
  /**
   * Family-expanded document count — what an export built from these same
   * filters actually contains, attachments and earlier thread emails included.
   * Always >= total_families (#762).
   *
   * When `total_documents_exact` is false this is a FLOOR, not the total: the
   * server stops counting at `export_limit + 1` because counting the whole
   * expansion is unbounded work for a number nobody can act on (the export
   * refuses above the limit anyway). Render it as "more than N", never as N.
   */
  total_documents: number;
  /** False when `total_documents` is a floor rather than the real total. */
  total_documents_exact: boolean;
  /** The server's export row cap, for the "narrow your filters" copy. */
  export_limit: number;
}

/**
 * The two numbers stated before a download: `N families · M documents`.
 *
 * A separate call from the table fetch on purpose — the expanded count is a
 * join across the whole filtered corpus, so it is paid for when the export menu
 * opens, not on every page turn, sort and filter keystroke.
 */
export async function fetchFilteredDocsCounts(
  caseId: string,
  params: DocumentSearchParams,
): Promise<SearchCountsResponse> {
  return api.post<SearchCountsResponse>(`/cases/${caseId}/documents/search/counts`, params);
}

export interface ExportResponse {
  columns: string[];
  rows: any[][];
  /**
   * Family-expanded DOCUMENT count — equal to `rows.length`, and the number
   * the server's 50K cap is enforced against. The workbook ships every family
   * member (attachments, earlier thread emails), so this is larger than the
   * row count shown on the review screen (#762 Defect 2).
   */
  total: number;
  /** Parent/family count — what the review screen's row count shows. */
  total_families: number;
}

export async function fetchFilteredDocsExport(
  caseId: string,
  params: DocumentSearchParams,
): Promise<ExportResponse> {
  return api.post<ExportResponse>(`/cases/${caseId}/documents/search/export`, params);
}

// ─── Discovery Requests & Responses ────────────────────────────────────────
// Types mirror the planned server response so call sites don't change when
// the real endpoints replace the mock-data shims below. See
// docs/discovery-requests-responses-feature.md for the backend shape.

export type DiscoveryType =
  | 'form_interrogatory'
  | 'special_interrogatory'
  | 'rfa'
  | 'doc_request';

export type ResponseType = 'substantive' | 'objection' | 'references_documents';

export type ItemStatus = 'current' | 'amended' | 'not_yet_received';

export const DISCOVERY_TYPE_LABELS: Record<DiscoveryType, string> = {
  form_interrogatory: 'Form Interrogatories',
  special_interrogatory: 'Special Interrogatories',
  rfa: 'Requests for Admission',
  doc_request: 'Document Requests',
};

export const RESPONSE_TYPE_LABELS: Record<ResponseType, string> = {
  substantive: 'Substantive',
  objection: 'Objection',
  references_documents: 'References Documents',
};

export const ITEM_STATUS_LABELS: Record<ItemStatus, string> = {
  current: 'Current',
  amended: 'Amended',
  not_yet_received: 'Not Yet Received',
};

export interface DiscoveryItemRow {
  id: string;
  responding_party: string;
  /** Canonical name of the propounding (requesting) party. Sourced from
   * the same case_parties row as responding_party, just via the other
   * FK on the item. Surfaces in the set-detail header label so the
   * reviewer sees who served the request alongside who has to answer. */
  propounding_party: string;
  set_number: string;
  discovery_type: DiscoveryType;
  item_number: string;
  question_text: string;
  /** Attorney scratch pad — deposition prep notes. Shared per-case, not per-user. */
  notes: string | null;
  /**
   * Attorney-set flag indicating the current response is inadequate
   * (typical prelude to a meet-and-confer). Cleared once an amendment
   * resolves the deficiency. Per-item, not per-response — see
   * migration 045 for the rationale.
   */
  is_deficient: boolean;
  /** ISO yyyy-mm-dd; nullable. Per-item M&C calendar marker — surfaces as
   * the "M&C {date}" pill in the drawer header. See migration 046. */
  mc_scheduled_date: string | null;
  /** Current response (latest amendment). Null when no response yet. */
  current_response_id: string | null;
  /** Reviewer-entered counts on the current response: how many docs the
   * party actually produced vs. how many we expect. Both nullable; the
   * drawer leaves them blank until someone fills them in. */
  produced_count: number | null;
  expected_count: number | null;
  response_text: string;
  objections_text: string | null;
  response_types: ResponseType[];
  status: ItemStatus;
  has_amendments: boolean;
  // Page range in the source response PDF. 1-based.
  source_page_start: number | null;
  source_page_end: number | null;
  // Provenance of the current response. served_date is when opposing
  // counsel served it (legal timeline); response_uploaded_at is when it
  // got loaded into Geniac (system timeline). All five are null when
  // the item has no response yet, or the response was entered manually
  // (no backing intake). response_intake_id is what the drawer hands
  // to the batch-details page when the user clicks the source link.
  served_date: string | null;
  response_intake_id: string | null;
  response_uploaded_by_email: string | null;
  response_uploaded_at: string | null;
  response_source_filename: string | null;
  /** Total comments on this item — drives the (n) badge on the Comments
   * tab without requiring the lazy comments fetch to resolve first. */
  comment_count: number;
  /** Total audit-log rows on this item — same pattern, drives the (n)
   * badge on the Activity tab. */
  activity_count: number;
  /** ISO yyyy-mm-dd; nullable. Captured during the upload flow's
   * Deadline Calculator on the request intake's config. Same value
   * for every item in the same set. Surfaces in the drawer as the
   * Due Date field and in the matrix as the Due Date column. */
  response_due_date: string | null;
}

export type DiscoverySortKey =
  | 'propounding_party'
  | 'responding_party'
  | 'set_number'
  | 'item_number'
  | 'question_text'
  | 'response_text'
  | 'response_types'
  | 'status';

export interface DiscoverySortConfig {
  key: DiscoverySortKey;
  direction: 'asc' | 'desc';
}

export interface DiscoverySearchParams {
  parties?: string[];
  /** Propounding (requesting) party canonical names. Today only the
   * matrix click writes this — there's no UI surface for setting it
   * directly. Used to disambiguate (responding, set, type) tuples
   * when more than one propounding party served on the same
   * combination. */
  propounding_parties?: string[];
  sets?: string[];
  discovery_types?: DiscoveryType[];
  response_types?: ResponseType[];
  statuses?: ItemStatus[];
  // Filter to responses that came from one of these source PDF filenames
  // (matched via the current response's intake). Empty/omitted = no filter.
  documents?: string[];
  search?: string;
  sort?: DiscoverySortConfig | null;
}

// One intake's source PDF surfaced in the sidebar's document picker.
// One entry per confirmed intake (two uploads of the same filename are
// two entries). set_number + received_date are "best-effort" — they're
// pulled from the intake's config JSONB, which the user fills in at
// upload time. Missing entries fall back to the intake's created_at for
// ordering, and the UI just hides the subtitle when nothing useful is
// available. intake_id is what View / Delete actions on the picker
// target — lets the sidebar hand the details page or deleteIntake the
// exact row, even when filenames collide.
export interface DiscoveryDocument {
  intake_id: string;
  filename: string;
  set_number: string | null;
  received_date: string | null;
}

export interface DiscoveryMeta {
  parties: string[];
  sets: string[];
  // Distinct source PDFs across confirmed intakes in the case, ordered
  // set → date. The sidebar's `< >` picker steps through them.
  documents: DiscoveryDocument[];
  total: number;
}

export interface DiscoverySearchResult {
  rows: DiscoveryItemRow[];
  syntax_kind: 'plain' | 'boolean' | 'fallback';
  truncated: boolean;
  limit: number;
}

// `include_search_metadata` is opt-in on the API, so a backend that predates
// #867 ignores it and answers with the bare array. The three release workflows
// are tag-triggered in parallel — nothing orders api before frontend — so that
// pairing is reachable for the couple of minutes a deploy takes, and on staging
// for as long as a master push takes to land both. Normalising here rather than
// requiring a deploy order means the worst case is a search that behaves like
// the old one until the API catches up, instead of a table that renders nothing.
export async function fetchDiscoveryItems(
  caseId: string,
  params: DiscoverySearchParams,
  signal?: AbortSignal,
): Promise<DiscoverySearchResult> {
  const body = await api.post<DiscoverySearchResult | DiscoveryItemRow[]>(
    `/cases/${caseId}/discovery-items/search`,
    { ...params, include_search_metadata: true }, { signal });
  return Array.isArray(body)
    ? { rows: body, syntax_kind: 'plain', truncated: false, limit: 2000 }
    : body;
}

export async function exportDiscoveryItems(
  caseId: string,
  params: DiscoverySearchParams,
): Promise<Blob> {
  return api.postBlob(`/cases/${caseId}/discovery-items/export`, params);
}

// Optional set-focus filters that scope the `documents` portion of the
// meta payload. parties/sets/total stay case-wide regardless. Empty
// arrays = no filter on that dimension.
export interface DiscoveryMetaParams {
  propounding?: string[];
  parties?: string[];
  sets?: string[];
  types?: DiscoveryType[];
}

export async function fetchDiscoveryMeta(
  caseId: string,
  params?: DiscoveryMetaParams,
): Promise<DiscoveryMeta> {
  const qs = new URLSearchParams();
  for (const v of params?.propounding ?? []) qs.append('propounding', v);
  for (const v of params?.parties ?? []) qs.append('parties', v);
  for (const v of params?.sets ?? []) qs.append('sets', v);
  for (const v of params?.types ?? []) qs.append('types', v);
  const query = qs.toString();
  const path = `/cases/${caseId}/discovery-items/meta${query ? `?${query}` : ''}`;
  return api.get<DiscoveryMeta>(path);
}

// ─── Sets matrix (Plaintiffs' / Defendants' Requests landing) ─────────────

export type DiscoverySetSide = 'plaintiffs' | 'defendants';
export type DiscoverySetTone = 'green' | 'amber' | 'red' | 'gray' | 'blue';

/** One intake (uploaded batch — request, response, or amendment) tied to
 * a set. Surfaces in the matrix's chevron expansion as a sub-row per
 * intake, oldest first. An intake "belongs" to a set if it either
 * created items in that set (request path: discovery_items.intake_id)
 * or carried responses to items in that set (response path:
 * discovery_item_responses.intake_id). */
export interface DiscoverySetIntake {
  intake_id: string;
  filename: string | null;
  /** Date opposing counsel served the document, from intake.config.date_sent.
   * Null when paralegal hasn't entered it yet (rare). */
  served_date: string | null;
  /** Wall-clock ISO when the intake row was created (paralegal upload time). */
  created_at: string;
  /** Distinct items in this set that this intake touched — either by
   * creating them (request) or carrying their responses (amendment). */
  item_count: number;
}

/** One row in the matrix view = one (propounding, responding, set, type) tuple. */
export interface DiscoverySetRow {
  propounding_party_id: string;
  propounding_party_name: string;
  propounding_side: DiscoverySetSide;
  responding_party_id: string;
  responding_party_name: string;
  set_number: string;
  set_number_normalized: string;
  discovery_type: DiscoveryType;
  request_count: number;
  deficient_count: number;
  produced_count: number;
  unanswered_count: number;
  status_label: string;
  status_tone: DiscoverySetTone;
  /** ISO yyyy-mm-dd; nullable. Captured on the request intake's
   * config (Deadline Calculator) and surfaced as the Due column on
   * the matrix landing screen, color-coded for urgency. The matrix
   * sorts by this ASC NULLS LAST so the most-urgent rows land at top. */
  response_due_date: string | null;
  /** All confirmed intakes tied to this set, oldest first. An intake
   * shows up here whether it created the questions (request path) or
   * carried responses (response / amendment path) — see the union in
   * `intake_set_links` (api/routes/discovery_items.py). Cancelled
   * intakes are filtered out by the backend's `set_intakes` CTE.
   * Empty array when no confirmed intakes reference the set. */
  intakes: DiscoverySetIntake[];
}

export async function fetchDiscoverySets(caseId: string): Promise<DiscoverySetRow[]> {
  return api.get<DiscoverySetRow[]>(`/cases/${caseId}/discovery-sets`);
}

// ─── Version history + cross-party comparison ─────────────────────────────

/** One row of discovery_item_responses, projected for the UI. */
export interface DiscoveryItemVersion {
  id: string;
  item_id: string;
  served_date: string | null;       // ISO yyyy-mm-dd
  document_label: string;           // verbatim: "Responses" | "Amended Responses" | "After Meet and Confer" | "Court-Compelled" ...
  response_text: string;
  objections_text: string | null;
  response_types: ResponseType[];
  source_doc_filename: string | null;
  source_page_start: number | null;
  source_page_end: number | null;
  is_current: boolean;
}

/** One party's responses to the same question, grouped for comparison view. */
export interface PartyResponseForQuestion {
  party: string;
  status: ItemStatus;
  versions: DiscoveryItemVersion[]; // chronological asc; last element is current
}

/** All versions of a specific (party, set, item_number) combination. */
export async function fetchItemVersions(itemId: string): Promise<DiscoveryItemVersion[]> {
  return api.get<DiscoveryItemVersion[]>(`/discovery-items/${itemId}/versions`);
}

export async function patchDiscoveryItem(
  itemId: string,
  patch: {
    notes?: string | null;
    is_deficient?: boolean;
    mc_scheduled_date?: string | null;
  },
): Promise<{
  id: string;
  notes: string | null;
  is_deficient: boolean;
  mc_scheduled_date: string | null;
}> {
  return api.patch(`/discovery-items/${itemId}`, patch);
}

/** Update reviewer-entered produced/expected counts on a response
 * version. Either field may be null to clear. */
export async function patchDiscoveryResponse(
  responseId: string,
  patch: { produced_count?: number | null; expected_count?: number | null },
): Promise<{
  id: string;
  item_id: string;
  produced_count: number | null;
  expected_count: number | null;
}> {
  return api.patch(`/discovery-responses/${responseId}`, patch);
}

/**
 * One audit_log row surfaced in the Activity tab. Narrowed to the
 * three sources the item lifecycle spans: the item, its responses,
 * and the intake_items that produced them.
 */
export interface DiscoveryItemAuditEntry {
  id: string;
  action: string;
  entity_type:
    | 'discovery_item'
    | 'discovery_item_response'
    | 'intake_item';
  entity_id: string | null;
  user_email: string;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  created_at: string;
}

export async function fetchDiscoveryItemAudit(
  itemId: string,
): Promise<DiscoveryItemAuditEntry[]> {
  return api.get<DiscoveryItemAuditEntry[]>(
    `/discovery-items/${itemId}/audit`,
  );
}

/**
 * One comment on a discovery item. The unit of discussion is the
 * question, so comments span every response version. `viewed_response_id`
 * is a snapshot of which response the author was looking at when posting
 * (null = the question only / no response yet) — used by the UI to render
 * a small "Current Response" / earlier-version stamp so readers know
 * what the comment was reacting to. See migration 046.
 */
export interface DiscoveryItemComment {
  id: string;
  item_id: string;
  viewed_response_id: string | null;
  author_email: string;
  author_name: string | null;
  content: string;
  created_at: string;
  /** Server-stamped on every UPDATE; equal to created_at when never edited. */
  updated_at: string;
}

export async function fetchDiscoveryItemComments(
  itemId: string,
): Promise<DiscoveryItemComment[]> {
  return api.get<DiscoveryItemComment[]>(
    `/discovery-items/${itemId}/comments`,
  );
}

export async function postDiscoveryItemComment(
  itemId: string,
  viewedResponseId: string | null,
  content: string,
): Promise<DiscoveryItemComment> {
  return api.post<DiscoveryItemComment>(
    `/discovery-items/${itemId}/comments`,
    { viewed_response_id: viewedResponseId, content },
  );
}

export async function patchDiscoveryItemComment(
  commentId: string,
  content: string,
): Promise<DiscoveryItemComment> {
  return api.patch<DiscoveryItemComment>(
    `/discovery-comments/${commentId}`,
    { content },
  );
}

export async function deleteDiscoveryItemComment(
  commentId: string,
): Promise<{ ok: true; id: string }> {
  return api.delete<{ ok: true; id: string }>(`/discovery-comments/${commentId}`);
}

/** All parties' responses to the same question (same set_number + discovery_type + item_number). */
export async function fetchSiblingResponses(itemId: string): Promise<{
  question_text: string;
  discovery_type: DiscoveryType;
  set_number: string;
  item_number: string;
  parties: PartyResponseForQuestion[];
}> {
  return api.get<{
    question_text: string;
    discovery_type: DiscoveryType;
    set_number: string;
    item_number: string;
    parties: PartyResponseForQuestion[];
  }>(`/discovery-items/${itemId}/comparison`);
}

// ─── Discovery Intake (upload → extraction → review → confirm) ─────────────

export type IntakeStatus =
  | 'extracting'
  | 'ready_for_review'
  | 'confirmed'
  | 'failed'
  | 'cancelled';

export type ReviewState = 'as_extracted' | 'edited' | 'confirmed' | 'flagged';

export interface CaseParty {
  id: string;
  case_id: string;
  canonical_name: string;
  short_name: string | null;
  roles: string[];
  aliases: string[];
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type CasePartyPatch = Partial<
  Pick<CaseParty, 'canonical_name' | 'short_name' | 'roles' | 'aliases' | 'notes'>
>;

export interface IntakeItem {
  /** Row id from `discovery_intake_items`. Used as the PATCH path param. */
  id: string;
  item_number: string;
  question_text: string;
  response_text: string;
  objections_text: string | null;
  response_types: ResponseType[];
  review_state: ReviewState;
  reviewer_note: string | null;
  // Page range in the source PDF. pdfplumber enumerates pages natively, so
  // Claude is prompted to record the starting and ending page per item.
  source_page: number | null; // kept for backwards compatibility; equals source_page_start
  source_page_start: number | null;
  source_page_end: number | null;
  // LLM uncertainty signal, set at extraction time when Claude guessed
  // at the question/response/objection split. Read-only — the paralegal
  // cannot mutate these via PATCH. Items with extraction_flag='uncertain'
  // arrive pre-seeded with review_state='flagged' so they surface through
  // the existing flag UI.
  extraction_flag: 'uncertain' | null;
  extraction_flag_reason: string | null;
  /** The normalized `item_number` this row carried before the FIRST
   *  reviewer correction; null means never corrected (migration 123).
   *  Non-null is what lets the card say what the extraction actually
   *  read, so a correction is visible rather than just applied. */
  item_number_as_extracted: string | null;
  /** Reviewer decision to leave this draft row out of the case record.
   *  The row, its text and its provenance survive — confirm's duplicate
   *  scan and its materialization loop both skip it. */
  excluded_from_confirm: boolean;
  /** UI convenience; full edit history lives in audit_log. */
  last_edited_by_email?: string | null;
  last_edited_at?: string | null;
}

export interface IntakeConfig {
  discovery_type: DiscoveryType;
  interrogatory_subtype?: 'form' | 'special' | null;
  responding_party_id: string | null; // null when creating a new party
  responding_party_name: string;
  // Propounding party (who's asking). Optional on older intakes — the
  // backend's /preview endpoint extracts it from the PDF caption and the
  // config form persists it here. Confirm requires one of these.
  propounding_party_id?: string | null;
  propounding_party_name?: string | null;
  set_number: string;
  /** Service date — when the propounding party served the requests.
   * Anchors the Deadline Calculator's response-due math (CCP 30-day
   * clock starts on service). Optional; nullable. */
  date_sent: string | null;
  /** ISO yyyy-mm-dd; the date by which the responding party must serve
   * responses. Reviewer-entered via the Deadline Calculator widget on the
   * intake form. Optional — older intakes won't have it. */
  response_due_date?: string | null;
  source_filename: string;
  source_filesize?: number | null; // bytes, sent alongside filename for dedup tiebreaker
}

export interface PartyResolution {
  extracted_name: string;
  suggested_party_id: string | null;
  chosen_party_id: string | null;
  create_new: boolean;
  new_party_name: string | null;
}

export interface DiscoveryIntake {
  id: string;
  case_id: string;
  status: IntakeStatus;
  config: IntakeConfig;
  items: IntakeItem[];
  party_resolution: PartyResolution | null;
  error_msg: string | null;
  created_at: string;
  confirmed_at: string | null;
  // Set on first confirm, never cleared on reopen. The review panel uses
  // this to distinguish first-confirm wording from re-confirm-after-edit
  // wording.
  first_confirmed_at: string | null;
  // Email of the user who dropped the file. Shown on the batch details
  // page; returned by GET /discovery-intakes/{id} (not always present on
  // older intakes that predate the column).
  uploaded_by_email: string | null;
  // Server-derived liveness signals for the streaming view. `is_stale`
  // is true iff the intake has been `extracting` with no item progress
  // for the staleness window (5 min server-side at time of writing).
  // The streaming banner branches on this to surface a Restart action.
  // `last_progress_at` is `MAX(item.created_at)` falling through to
  // intake.created_at — useful for "no progress in N minutes" copy.
  last_progress_at: string;
  is_stale: boolean;
  // Per-chunk extraction progress (Outcome 4 in
  // `plan-luminous-honking-origami.md`). Both fields are populated by
  // the Fargate worker once it knows how many chunks the PDF was sliced
  // into. They may stay NULL for the first few seconds of an extraction
  // (Textract still running, no chunking yet), and for older intakes
  // that predate the column — UI must tolerate null gracefully and
  // fall back to the generic "Extracting…" copy.
  chunks_total: number | null;
  chunks_completed: number | null;
}

// #428 — one cross-matter "needs attention" intake row, as returned by
// GET /me/intakes/exposed. The server buckets each row into one of three
// exposure states (failed / stalled / aging); `case_ref` is the URL slug
// used to deep-link into that matter's All Files view.
export interface ExposedIntake {
  case_id: string;
  case_ref: string;
  case_name: string;
  intake_id: string;
  status: IntakeStatus;
  bucket: 'failed' | 'stalled' | 'aging';
  created_at: string;
  error_code: string | null;
}

// Cross-matter exposed intakes for the Cases-screen banner (#428).
// RLS-scoped server-side; returns at most 20 rows, newest first, alongside the
// TRUE uncapped exposed-intake count (`total`) and a `capped` flag so the banner
// can render an "N+" overflow signal instead of under-reporting the row count
// when more than 20 intakes are dangling.
export interface ExposedIntakesResult {
  rows: ExposedIntake[];
  total: number;
  capped: boolean;
}
export async function fetchMyExposedIntakes(): Promise<ExposedIntakesResult> {
  const { rows, total, capped } = await api.get<{
    rows: ExposedIntake[];
    total?: number;
    capped?: boolean;
  }>('/me/intakes/exposed');
  const safeRows = rows ?? [];
  const safeTotal = typeof total === 'number' ? total : safeRows.length;
  return {
    rows: safeRows,
    total: safeTotal,
    capped: typeof capped === 'boolean' ? capped : safeTotal > safeRows.length,
  };
}

export interface ItemPatch {
  question_text?: string;
  response_text?: string;
  objections_text?: string | null;
  response_types?: ResponseType[];
  review_state?: ReviewState;
  reviewer_note?: string | null;
  /** Correct which request this answer belongs to (#1157). Normalized
   *  server-side through the same function the extractor's headings go
   *  through, so a typed "no. 6" and an extracted "No. 6" become one
   *  string — confirm's duplicate scan matches exact strings. Refused
   *  with 409 `duplicate_item_number` if another non-excluded item in
   *  the same upload already holds it. */
  item_number?: string;
  /** Leave this row out of the case record, or put it back (#1157).
   *  Sent on its own — neither this nor `item_number` is a content edit,
   *  so neither flips `review_state` to 'edited'. */
  excluded_from_confirm?: boolean;
}

// ─── Intake backend ───────────────────────────────────────────────────────
// Since the refactor to row-per-item, the backend returns items as a flat
// array joined from `discovery_intake_items`. _mapIntake is now a thin
// shape adjuster (just fills in `source_page` for legacy consumers).

interface RawIntake {
  id: string;
  case_id: string;
  status: IntakeStatus;
  config: IntakeConfig;
  items: IntakeItem[];
  party_resolution: PartyResolution | null;
  error_msg: string | null;
  created_at: string;
  confirmed_at: string | null;
  first_confirmed_at?: string | null;
  uploaded_by_email?: string | null;
  last_progress_at?: string | null;
  is_stale?: boolean;
  chunks_total?: number | null;
  chunks_completed?: number | null;
}

function _mapIntake(raw: RawIntake): DiscoveryIntake {
  const items = (raw.items ?? []).map(
    (it): IntakeItem => ({
      ...it,
      source_page: it.source_page_start ?? null,
    }),
  );
  return {
    id: raw.id,
    case_id: raw.case_id,
    status: raw.status,
    config: raw.config,
    items,
    party_resolution: raw.party_resolution,
    error_msg: raw.error_msg,
    created_at: raw.created_at,
    confirmed_at: raw.confirmed_at,
    first_confirmed_at: raw.first_confirmed_at ?? null,
    uploaded_by_email: raw.uploaded_by_email ?? null,
    // Older intakes / legacy responses won't carry these. Default to
    // "not stale, last progress = upload time" so callers don't have to
    // null-check.
    last_progress_at: raw.last_progress_at ?? raw.created_at,
    is_stale: raw.is_stale ?? false,
    chunks_total: raw.chunks_total ?? null,
    chunks_completed: raw.chunks_completed ?? null,
  };
}

export async function requestUploadUrl(
  caseId: string,
  filename: string,
): Promise<{ url: string; s3_key: string }> {
  const resp = await api.post<{ upload_url: string; s3_key: string }>(
    `/cases/${caseId}/discovery-intakes/presign`,
    { filename, content_type: 'application/pdf' },
  );
  return { url: resp.upload_url, s3_key: resp.s3_key };
}

export async function fetchIntakeSourceUrl(intakeId: string): Promise<string> {
  const resp = await api.get<{ url: string }>(
    `/discovery-intakes/${intakeId}/source-url`,
  );
  return resp.url;
}

export async function fetchCaseParties(caseId: string): Promise<CaseParty[]> {
  return api.get<CaseParty[]>(`/cases/${caseId}/parties`);
}

export async function updateCaseParty(
  caseId: string,
  partyId: string,
  patch: CasePartyPatch,
): Promise<CaseParty> {
  return api.patch<CaseParty>(`/cases/${caseId}/parties/${partyId}`, patch);
}

export async function deactivateCaseParty(caseId: string, partyId: string): Promise<void> {
  await api.delete<void>(`/cases/${caseId}/parties/${partyId}`);
}

export async function mergeCaseParty(
  caseId: string,
  sourceId: string,
  targetId: string,
): Promise<CaseParty> {
  return api.post<CaseParty>(`/cases/${caseId}/parties/${sourceId}/merge`, {
    target_party_id: targetId,
  });
}

export async function createDiscoveryIntake(
  caseId: string,
  config: IntakeConfig,
  s3_key: string,
): Promise<DiscoveryIntake> {
  const raw = await api.post<RawIntake>(
    `/cases/${caseId}/discovery-intakes`,
    { s3_key, config },
  );
  return _mapIntake(raw);
}

export async function fetchDiscoveryIntake(intakeId: string): Promise<DiscoveryIntake> {
  const raw = await api.get<RawIntake>(`/discovery-intakes/${intakeId}`);
  return _mapIntake(raw);
}

// ─── Intake pre-submit helpers ────────────────────────────────────────────
// These run at drop time, before the S3 upload. Both are best-effort —
// failures do not block the user from proceeding with manual entry.

export interface IntakePreviewParsed extends Partial<IntakeConfig> {
  /** LLM-resolved party uuids (from the case's active parties) when the caption matched confidently. */
  propounding_party_id?: string;
  responding_party_id?: string;
  /** Raw caption strings as a fallback when no id match was confident. */
  propounding_party_name?: string;
  responding_party_name?: string;
}

export interface IntakePreviewResponse {
  parsed: IntakePreviewParsed;
  warnings: string[];
}

export async function previewIntake(
  caseId: string,
  text: string,
  pageCount: number,
): Promise<IntakePreviewResponse> {
  return api.post<IntakePreviewResponse>(
    `/cases/${caseId}/discovery-intake/preview`,
    { text, page_count: pageCount },
  );
}

export interface FilenameMatch {
  intake_id: string;
  source_filename: string;
  source_filesize: number | null;
  size_matches: boolean | null; // null when either side lacks size info
  status: IntakeStatus;
  created_at: string;
  created_by_email: string;
  item_count: number;
  // True when filename+size both match AND the existing intake is in a
  // live status (extracting / ready_for_review / confirmed) — the upload
  // flow halts and shows a hard-block modal in that case (#345).
  blocking: boolean;
}

export async function checkIntakeFilename(
  caseId: string,
  filename: string,
  size?: number,
): Promise<{ matches: FilenameMatch[] }> {
  return api.post<{ matches: FilenameMatch[] }>(
    `/cases/${caseId}/discovery-intake/check-filename`,
    { filename, size },
  );
}

// Resumable intakes for this case — anything the user could still pick up
// (extracting / ready_for_review / failed). Shape mirrors FilenameMatch
// plus a few fields so the upload screen can show a "you have an upload in
// progress" banner and hand it back through `onOpenExistingIntake`.
export interface ResumableIntake {
  intake_id: string;
  status: IntakeStatus;
  source_filename: string | null;
  discovery_type: DiscoveryType | null;
  set_number: string | null;
  responding_party_name: string | null;
  item_count: number;
  created_at: string;
  created_by_email: string;
  error_msg: string | null;
}

export async function listResumableIntakes(caseId: string): Promise<ResumableIntake[]> {
  const resp = await api.get<{ intakes: ResumableIntake[] }>(
    `/cases/${caseId}/discovery-intakes?resumable=1`,
  );
  return resp.intakes;
}

// ─── All Files tab (matter-wide intake list) ──────────────────────────────
// The default branch of GET /cases/{id}/discovery-intakes (the same route
// the legacy `resumable=1` shim uses, but a DIFFERENT response shape). The
// server projects raw (status, is_stale, failure_class) onto a single
// user-facing `display_status` pill — the FE never re-derives it. This is
// the foundation for the All Files view (plan: dapper-dreaming-sifakis.md F1/F3).
//
// The shim (`resumable=1`) still returns `ResumableIntake` (raw error_msg,
// 3 statuses) for the upload-screen banner; the two shapes share one route.
// Keep `DiscoveryFileRow` byte-aligned with the backend allowlist (F5 export
// hygiene test asserts the key set) — `error_msg` is deliberately NOT here.

// The seven user-facing pills. Server-derived from raw status + is_stale +
// failure_class. `stalled` and `unusable_file` have no raw-status equivalent.
export type DiscoveryDisplayStatus =
  | 'processing'
  | 'stalled'
  | 'needs_review'
  | 'confirmed'
  | 'failed'
  | 'unusable_file'
  | 'cancelled';

// Filter-chip buckets. `all` is the no-filter default; the per-pill buckets
// map 1:1 to display_status. `needs_attention` is a META-bucket (the plan's
// "Needs attention" quick-pick) = Stalled ∪ Failed ∪ Unusable file — it has
// no standalone display_status; the toolbar/health-badge use it so a click
// always lands on a non-empty, correct filter. The toolbar chips are
// single-select.
export type FileBucket = 'all' | DiscoveryDisplayStatus | 'needs_attention';

export const FILE_BUCKETS: readonly FileBucket[] = [
  'all',
  'needs_attention',
  'processing',
  'needs_review',
  'confirmed',
  'failed',
  'unusable_file',
  'cancelled',
] as const;

// The display_status pills the `needs_attention` meta-bucket expands to when
// sent to the server (`status` CSV). Aging-unconfirmed is a time refinement
// of `needs_review`, not a display_status, so it is NOT included here — the
// header badge's Attention tier points at the `needs_review` bucket directly
// for that case. Keeping the meta-bucket to concrete display_status values
// means it round-trips cleanly through the documented `status` CSV param.
export const NEEDS_ATTENTION_BUCKETS: readonly DiscoveryDisplayStatus[] = [
  'stalled',
  'failed',
  'unusable_file',
] as const;

// One row in the All Files table. Keys MUST stay in sync with the backend
// allowlist in handle_list_intakes (default branch) — see F5 export-hygiene.
export interface DiscoveryFileRow {
  intake_id: string;
  // Server-derived pill (single source of truth). `stalled` here means raw
  // status `extracting` + is_stale; the FE never re-derives it.
  display_status: DiscoveryDisplayStatus;
  // Raw lifecycle status + the stale sub-state — carried for action-gating
  // only (never for re-deriving the pill). `raw_status` is the DB column.
  raw_status: IntakeStatus;
  is_stale: boolean;
  source_filename: string | null;
  // Best-effort label built server-side from config (e.g. "Set 1 · RFA ·
  // Example Co."). Null for rows with no declared request metadata.
  discovery_request_label: string | null;
  set_number: string | null;
  discovery_type: DiscoveryType | null;
  propounding_party_name: string | null;
  responding_party_name: string | null;
  // Dual semantics: "N found" (provisional, discovery_intake_items) while
  // processing; "N in record" (materialized) once confirmed. Renderer labels
  // it per display_status.
  item_count: number;
  served_date: string | null;       // from config (date_sent)
  response_due_date: string | null; // from config
  created_at: string;
  confirmed_at: string | null;
  created_by_email: string | null;
  confirmed_by_email: string | null;
  // Confirmed rows only — count of items the human confirmed over a flagged
  // / as_extracted state (supervision signal). 0 otherwise.
  flagged_item_count: number;
  chunks_total: number | null;
  chunks_completed: number | null;
  // Parsed server-side from error_msg (defensive). Default TRUE — set false
  // ONLY when the parsed error JSON has `retryable: false`. Mirrors
  // handle_retry_intake so the FE never shows a Retry that the backend 409s.
  retryable: boolean;
  // Parsed `error_msg.code` (string) or null — never the raw message. Drives
  // the conditional "Accept incomplete" affordance (dormant: nothing emits
  // 'missing_items' today).
  error_code: string | null;
}

export interface DiscoveryFilesResponse {
  rows: DiscoveryFileRow[];
  total_count: number;
  // Per-bucket counts computed BEFORE the status filter (so the chips don't
  // lie when one is selected). Keyed by display_status.
  bucket_counts: Partial<Record<DiscoveryDisplayStatus, number>>;
  // `needs_review` rows older than the aging threshold (subset of the
  // needs_review bucket). Drives the header badge Attention tier.
  aging_unconfirmed_count: number;
  page: number;
  page_size: number;
}

export type DiscoveryFilesSort =
  | 'created_desc'
  | 'created_asc'
  | 'filename_asc'
  | 'status';

export interface DiscoveryFilesParams {
  page?: number;
  pageSize?: number;
  // Single bucket (omitted / 'all' → no status filter). Sent as the `status`
  // CSV param the backend expects.
  bucket?: FileBucket;
  q?: string;
  sort?: DiscoveryFilesSort;
}

export async function fetchDiscoveryFiles(
  caseId: string,
  params: DiscoveryFilesParams = {},
): Promise<DiscoveryFilesResponse> {
  const qs = new URLSearchParams();
  if (params.page && params.page > 1) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('page_size', String(params.pageSize));
  if (params.bucket && params.bucket !== 'all') {
    // `needs_attention` is a meta-bucket — expand to the CSV of concrete
    // display_status values the server's `status` filter understands. Every
    // other bucket is itself a display_status and passes through verbatim.
    const statusParam =
      params.bucket === 'needs_attention'
        ? NEEDS_ATTENTION_BUCKETS.join(',')
        : params.bucket;
    qs.set('status', statusParam);
  }
  if (params.q && params.q.trim()) qs.set('q', params.q.trim());
  if (params.sort && params.sort !== 'created_desc') qs.set('sort', params.sort);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get<DiscoveryFilesResponse>(
    `/cases/${caseId}/discovery-intakes${suffix}`,
  );
}

// Re-upload an `unusable_file` intake: route the user back to the upload
// flow with the failed intake's declared discovery-request metadata
// pre-filled. The actual prefill is carried client-side via location.state —
// no backend call. This helper just shapes the config subset the upload form
// can seed from. Kept here next to the row type so the seam is documented.
export function reuploadConfigFromRow(
  row: DiscoveryFileRow,
): Partial<IntakeConfig> {
  return {
    discovery_type: row.discovery_type ?? undefined,
    responding_party_name: row.responding_party_name ?? undefined,
    propounding_party_name: row.propounding_party_name ?? undefined,
    set_number: row.set_number ?? undefined,
    date_sent: row.served_date ?? undefined,
    response_due_date: row.response_due_date ?? undefined,
  } as Partial<IntakeConfig>;
}

export async function patchIntakeItem(
  intakeId: string,
  itemId: string,
  patch: ItemPatch,
): Promise<DiscoveryIntake> {
  const raw = await api.patch<RawIntake>(
    `/discovery-intakes/${intakeId}/items/${itemId}`,
    patch,
  );
  return _mapIntake(raw);
}

export async function confirmAllIntakeItems(
  intakeId: string,
): Promise<{ intake: DiscoveryIntake; confirmedCount: number }> {
  const resp = await api.post<{ intake: RawIntake; confirmed_count: number }>(
    `/discovery-intakes/${intakeId}/items/confirm-all`,
    {},
  );
  return {
    intake: _mapIntake(resp.intake),
    confirmedCount: resp.confirmed_count,
  };
}

export async function patchIntakePartyResolution(
  intakeId: string,
  resolution: PartyResolution,
): Promise<DiscoveryIntake> {
  const raw = await api.patch<RawIntake>(
    `/discovery-intakes/${intakeId}`,
    { party_resolution: resolution },
  );
  return _mapIntake(raw);
}

/** What a confirm actually wrote (#812).
 *
 *  `responses_added` counts one per item the materialization loop processed,
 *  which is NOT the number of rows written: a response the insert discarded
 *  under `ON CONFLICT … DO NOTHING` was counted here too, and the user was
 *  told it had been saved. `responses_persisted` and `responses_suppressed`
 *  come from `RETURNING`, so they describe rows rather than attempts.
 *
 *  Both honest fields are optional: an older API that predates #812 returns
 *  neither, and the caller falls back rather than reporting zero. */
export interface ConfirmIntakeResult {
  items_created: number;
  responses_added: number;
  responses_persisted?: number;
  responses_suppressed?: number;
  items_total?: number;
  items_excluded?: number;
}

export async function confirmIntake(
  intakeId: string,
  body: {
    propounding_party_id?: string | null;
    propounding_party_name?: string | null;
    responding_party_id?: string | null;
    responding_party_name?: string | null;
  } = {},
): Promise<ConfirmIntakeResult> {
  return api.post<ConfirmIntakeResult>(
    `/discovery-intakes/${intakeId}/confirm`,
    body,
  );
}

export async function cancelIntake(intakeId: string): Promise<void> {
  await api.post<void>(`/discovery-intakes/${intakeId}/cancel`);
}

// Cooperative cancellation for an intake whose worker is mid-flight.
// Distinct from `cancelIntake`:
//   - `cancelIntake` (POST /cancel) is the legacy synchronous tear-down
//     used pre-Fargate when extraction lived in the API Lambda and the
//     status flip + item discard could happen in the request handler.
//   - `requestIntakeCancel` (POST /cancel-request) sets
//     `discovery_intakes.cancel_requested = TRUE` so the Fargate worker
//     checks the flag at chunk boundaries, commits any partial result,
//     flips status to 'cancelled', and exits cleanly. The UI shows a
//     "Cancelling…" optimistic state until the next poll returns the
//     terminal `cancelled` status.
//
// See Wave 4 of plan-luminous-honking-origami.md (portable wins,
// cancellation-via-stop-flag pattern).
//
// UNCERTAIN: the URL pattern `POST /discovery-intakes/{id}/cancel-request`
// matches the plan's open-questions section but should be verified with
// Producer B's implementation when wiring lands. Update here if the
// final endpoint shape differs.
export async function requestIntakeCancel(intakeId: string): Promise<void> {
  await api.post<void>(`/discovery-intakes/${intakeId}/cancel-request`);
}

// A retry re-extracts from scratch, which means deleting every staged
// item — and the reviewer's edits, notes and flags go with them. The
// server refuses the first such retry with 409
// `review_work_would_be_lost` and deletes nothing; `discardReviewWork`
// is the caller saying the paralegal has seen what is at stake and
// chosen to lose it. See #362.
export const RETRY_REVIEW_WORK_CODE = 'review_work_would_be_lost';

export type RetryReviewWorkItem = {
  item_number: string | null;
  review_state: string;
  /** Whether the item carries a reviewer note. The note text is
   *  deliberately not returned — case content does not belong in an
   *  error body, and "there is a note" is what the dialog needs. */
  has_note: boolean;
  last_edited_by_email: string | null;
};

export type RetryReviewWorkBlock = {
  /** Every reviewer-touched item, which may exceed `items.length`. */
  item_count: number;
  /** The first N, for naming them in the dialog. */
  items: RetryReviewWorkItem[];
  /** The server's sentence — it counts the edits/notes/flags at stake. */
  message: string;
};

/** Reads the 409 above off a thrown error, or null if it is any other
 *  failure. Keeps the `code` string in one place rather than in each of
 *  the three retry call sites. */
export function parseRetryReviewWorkBlock(e: unknown): RetryReviewWorkBlock | null {
  if (!(e instanceof ApiResponseError) || e.code !== RETRY_REVIEW_WORK_CODE) {
    return null;
  }
  const body = e.body ?? {};
  const rawItems = Array.isArray(body.items) ? body.items : [];
  return {
    item_count: typeof body.item_count === 'number' ? body.item_count : rawItems.length,
    items: rawItems as RetryReviewWorkItem[],
    message: typeof body.error === 'string' ? body.error : e.message,
  };
}

export async function retryIntake(
  intakeId: string,
  opts: { discardReviewWork?: boolean } = {},
): Promise<{ id: string; status: IntakeStatus }> {
  return api.post<{ id: string; status: IntakeStatus }>(
    `/discovery-intakes/${intakeId}/retry`,
    opts.discardReviewWork ? { discard_review_work: true } : {},
  );
}

// Override path for intakes the post-stream segmentation validator marked
// `failed (code=missing_items)`. Flips the intake to `ready_for_review`
// keeping the items intact, so the paralegal can confirm an extraction
// even though the validator detected additional numbered headings the
// model didn't pick up. Strictly scoped server-side to that one code —
// other failures (max_tokens_truncated, model_error, etc.) need a real
// retry, not paralegal acceptance. See #304 + #312.
export async function acceptIncompleteIntake(
  intakeId: string,
): Promise<{ id: string; status: IntakeStatus }> {
  return api.post<{ id: string; status: IntakeStatus }>(
    `/discovery-intakes/${intakeId}/accept-incomplete`,
    {},
  );
}

// Delete a confirmed or cancelled batch. Confirmed batches first tear down
// materialized responses and orphan questions; cancelled batches only remove
// the intake + editable intake items.
export async function deleteIntake(
  intakeId: string,
): Promise<{ id: string; responses_deleted: number; items_deleted: number }> {
  return api.delete<{ id: string; responses_deleted: number; items_deleted: number }>(
    `/discovery-intakes/${intakeId}`,
  );
}

// Flip a confirmed or cancelled batch back to `ready_for_review` so the user
// can edit it in the existing review panel. For confirmed batches, the server
// removes materialized rows so re-confirm doesn't produce duplicates;
// `discovery_intake_items` (the editable source of truth) stays intact.
export async function reopenIntake(
  intakeId: string,
): Promise<{ id: string; status: IntakeStatus; responses_deleted: number; items_deleted: number }> {
  return api.post<{ id: string; status: IntakeStatus; responses_deleted: number; items_deleted: number }>(
    `/discovery-intakes/${intakeId}/reopen`,
    {},
  );
}

// Structured error payload the backend writes into discovery_intakes.error_msg
// when extraction fails. Older rows may carry a plain string; callers must
// handle both shapes (see parseIntakeError below).
//
// MUST stay in sync with `ERROR_CODES` in `api/routes/discovery.py` —
// `api/tests/routes/test_extraction_error_codes.py` enforces this in CI by
// regex-extracting the union and asserting BE codes are a subset. The
// runtime guard in `parseIntakeError` is the second line of defence.
//
// A few codes are retained for back-compat with persisted error_msg
// rows from the pre-Textract pipeline: `pdf_too_long_for_multimodal`,
// `pdf_too_large_for_multimodal`, `missing_items`, `chunk_failed`. The
// new ingestion path doesn't emit them, but existing failed intakes
// may still surface them through the FE. UX handlers can be retired
// in a follow-up FE cleanup PR.
export type IntakeErrorCode =
  | 'pdf_unreadable'
  | 'pdf_too_large'
  | 'pdf_encrypted'
  | 'pdf_too_long_for_multimodal'
  | 'pdf_too_large_for_multimodal'
  | 'textract_failed'
  | 'model_error'
  | 'model_bad_request'
  | 'model_rate_limited'
  | 'model_overload'
  | 'model_credentials_rejected'
  | 'max_tokens_truncated'
  | 'chunk_too_large'
  | 'strict_mode_contract_violation'
  | 'empty_content_block_ids'
  | 'missing_items'
  | 'partial_extraction_failure'
  | 'chunk_failed'
  | 'no_items'
  | 'config_error'
  // Wave 16-C — typed StitchedExtractionError subclasses surfaced at the
  // discovery route boundary. Each carries forensic context backend-side
  // (the FE renders them with a generic backend-bug message in
  // DiscoveryIntakeFailedScreen since none are user-recoverable).
  | 'hydrator_block_id_not_found'
  | 'block_index_invalid_input'
  | 'bbox_continuity_block_id_missing'
  | 'coverage_gaps_unsorted_input'
  | 'unknown';

// Runtime mirror of the IntakeErrorCode union for `parseIntakeError` to
// validate against. Keep in sync with the union literal above — TS can't
// derive a runtime Set from a type union without a build step.
const KNOWN_INTAKE_ERROR_CODES: ReadonlySet<IntakeErrorCode> = new Set<IntakeErrorCode>([
  'pdf_unreadable',
  'pdf_too_large',
  'pdf_encrypted',
  'pdf_too_long_for_multimodal',
  'pdf_too_large_for_multimodal',
  'textract_failed',
  'model_error',
  'model_bad_request',
  'model_rate_limited',
  'model_overload',
  'model_credentials_rejected',
  'max_tokens_truncated',
  'chunk_too_large',
  'strict_mode_contract_violation',
  'empty_content_block_ids',
  'missing_items',
  'partial_extraction_failure',
  'chunk_failed',
  'no_items',
  'config_error',
  'hydrator_block_id_not_found',
  'block_index_invalid_input',
  'bbox_continuity_block_id_missing',
  'coverage_gaps_unsorted_input',
  'unknown',
]);

export interface IntakeError {
  code: IntakeErrorCode;
  message: string;
  retryable: boolean;
}

export function parseIntakeError(raw: string | null | undefined): IntakeError {
  if (!raw) {
    return { code: 'unknown', message: 'Unknown error', retryable: true };
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && typeof parsed.code === 'string') {
      const code = parsed.code as string;
      const message = String(parsed.message ?? 'Unknown error');
      const retryable = parsed.retryable !== false;
      if (KNOWN_INTAKE_ERROR_CODES.has(code as IntakeErrorCode)) {
        return { code: code as IntakeErrorCode, message, retryable };
      }
      // Backend emitted a code the FE doesn't know about. The CI drift
      // test should have caught this pre-merge; surfacing it at runtime
      // means a deploy went out where BE shipped ahead of FE. Log it
      // loud so it shows up in browser-console exports / Sentry, then
      // fall through to `unknown` so the user still gets a working
      // Retry button. See #312 D11.
      // eslint-disable-next-line no-console
      console.error(
        '[intake] backend emitted unrecognised error code',
        { code, message, retryable },
      );
      return { code: 'unknown', message, retryable };
    }
  } catch {
    // fall through — legacy plain-string error
  }
  return { code: 'unknown', message: raw, retryable: true };
}

// ─── Pleadings (docs/pleadings-feature.md) ────────────────────────────────
// Court-filing rows backed by `case_source_docs`. The wire shape is an
// explicit projection — `notes` (attorney work product) and the raw `s3_key`
// are NEVER serialized (spec Decision 11). The client gets `has_pdf` and
// opens the document through the short-lived `source-url` endpoint instead.

export type PleadingRelationshipField = 'version_of' | 'responds_to_id';
export interface PleadingRelationshipReference {
  source_page?: number;
  quote: string;
  title?: string;
  filed_on?: string;
}
export interface PleadingRelationshipSuggestion {
  status: 'matched' | 'needs_review';
  target_id: string | null;
  reference?: PleadingRelationshipReference;
  reason?: string;
  disagrees?: boolean;
}
export interface PleadingRelationshipState {
  source: 'human' | 'extracted' | 'inherited' | 'metadata';
  target_id: string | null;
  inherited_from?: string;
  reference?: PleadingRelationshipReference;
  proposal?: PleadingRelationshipSuggestion;
  conflict?: string;
  updated_by?: string | null;
  updated_at?: string;
}

export interface Pleading {
  id: string;
  /** snake_case kind from the server-owned taxonomy (e.g. `amended_complaint`). */
  kind: string;
  /**
   * Operative-version thread family (`complaint` / `answer` /
   * `cross_complaint` / `answer_to_cross_complaint`), derived server-side
   * from `kind`. Null for non-thread kinds (motions, orders, …) — those can
   * never be operative (spec Decision 15).
   */
  thread_kind: string | null;
  /** YYYY-MM-DD. Required on create/edit; null only on legacy rows (sorts last). */
  filed_on: string | null;
  title: string;
  filing_party: string | null;
  /** Best-effort server-side party resolution; free text is the display fallback. */
  filing_party_id: string | null;
  is_operative: boolean;
  has_pdf: boolean;
  /** Derived reverse lookup of `version_of` — the row that superseded this one. */
  superseded_by_id: string | null;
  superseded_by_title: string | null;
  uploaded_by_email: string | null;
  uploaded_at: string | null;
  updated_by_email: string | null;
  updated_at: string;
  /** Provenance: 'seed' | 'human' | 'ai_suggested' | 'ai_confirmed' (Decision 14). */
  metadata_source: string;
  /** The row THIS row supersedes (newer → older arrow, spec Decision 3). */
  version_of: string | null;
  responds_to_id?: string | null;
  relationship_state?: Partial<Record<PleadingRelationshipField, PleadingRelationshipState>>;
  created_at: string;
}

export interface PleadingsListResponse {
  pleadings: Pleading[];
  /** Valid pleading kinds, single-sourced server-side (PR #510 must-fix 4). */
  kinds: string[];
  /**
   * kind → operative-thread-family map (mirrors the DB's thread_kind
   * generated column). Kinds absent from the map have no thread and can
   * never be operative.
   */
  thread_kinds: Record<string, string>;
  /** Completeness signal: rows without an attached PDF (Decision 12). */
  missing_pdf_count: number;
}

export async function fetchPleadings(
  caseId: string,
): Promise<PleadingsListResponse> {
  return api.get<PleadingsListResponse>(`/cases/${caseId}/pleadings`);
}

export interface PleadingCreate {
  version_of?: string | null;
  responds_to_id?: string | null;
  relationship_preview_run_id?: string;
  kind: string;
  filed_on: string;
  title: string;
  /** Free-text filing party (display fallback for compound/off-roster filers). */
  filing_party?: string;
  /**
   * Party-picker selection — a `case_parties` uuid. When present the server
   * denormalizes `filing_party` to the party's canonical name (text + FK never
   * disagree); send `null` on PATCH to clear the link. Omit to use the
   * free-text `filing_party` path with best-effort match resolution (Decision 10).
   */
  filing_party_id?: string | null;
  is_operative?: boolean;
  /** From presignPleadingUpload; omit for a metadata-only row. */
  s3_key?: string;
  /**
   * AI-confirm signal (Phase 2). The `ai_run_id` returned by
   * {@link previewPleading}, echoed back to confirm that the saved metadata
   * came from — and was reviewed against — that AI suggestion. The SERVER owns
   * the resulting provenance flip (validates the run against `pleading_ai_runs`
   * and sets `metadata_source='ai_confirmed'` + the `metadata_confirmed_*`
   * columns); the client NEVER sets `metadata_source` directly. Omit when no AI
   * pre-fill survived to the saved row — the row then saves as 'human'.
   */
  ai_run_id?: string | null;
}

/** PATCH accepts any subset of the create fields (s3_key = attach/replace). */
export type PleadingPatch = Partial<PleadingCreate>;

/**
 * Create/PATCH response = the saved row plus write-only signals:
 * - `demoted_pleading_id`: the row an operative flip just superseded (so the UI
 *   can name it), present only when a flip demoted a sibling.
 * - `operative_flip_failed`: the row was saved but the operative toggle did NOT
 *   apply (the flip runs in its own transaction server-side and failed, e.g. a
 *   lock timeout). The row persisted; retry the toggle from Edit. Surfaced so
 *   the caller never reports an unqualified success when the flip silently
 *   didn't take.
 * - `vacated_scope` / `vacated_scope_filing_party`: a filing-party edit (PATCH,
 *   migration 092) moved an OPERATIVE cross_complaint/answer_to_cross_complaint
 *   row into a different scope group, leaving its OLD filing party's thread
 *   with zero operative rows — nothing auto-fills that gap, so the caller
 *   should tell the user which party's thread just went operative-less.
 */
export interface PleadingWriteResult extends Pleading {
  demoted_pleading_id?: string | null;
  operative_flip_failed?: boolean;
  vacated_scope?: boolean;
  vacated_scope_filing_party?: string | null;
}

export async function createPleading(
  caseId: string,
  input: PleadingCreate,
  signal?: AbortSignal,
): Promise<PleadingWriteResult> {
  return api.post<PleadingWriteResult>(`/cases/${caseId}/pleadings`, input, { signal });
}

export async function updatePleading(
  caseId: string,
  pleadingId: string,
  patch: PleadingPatch,
  signal?: AbortSignal,
): Promise<PleadingWriteResult> {
  return api.patch<PleadingWriteResult>(
    `/cases/${caseId}/pleadings/${pleadingId}`,
    patch,
    { signal },
  );
}

/**
 * Admin-only. Throws ApiResponseError with status 409 (body carries
 * `dependents: {claims, party_groups, signatories, superseding}`) when the
 * row is referenced downstream, 403 when the caller isn't a case admin.
 */
export async function deletePleading(
  caseId: string,
  pleadingId: string,
): Promise<void> {
  await api.delete<void>(`/cases/${caseId}/pleadings/${pleadingId}`);
}

/** Short-lived presigned GET for the as-filed PDF. 404 when the row has no PDF. */
export async function getPleadingSourceUrl(
  caseId: string,
  pleadingId: string,
): Promise<{ url: string; expires_in: number }> {
  return api.get<{ url: string; expires_in: number }>(
    `/cases/${caseId}/pleadings/${pleadingId}/source-url`,
  );
}

// ─── Pleading metadata pre-fill — Phase 2 (docs/pleadings-feature.md §Phase 2) ─
// Runs at file-stage time in the upload modal, before the S3 upload. Best-effort
// caption-block extraction over the PDF's first-page text — failures never block
// the user from filling the form by hand. Mirrors previewIntake (discovery).

/**
 * Confident caption fields from the preview call. Every field optional —
 * Claude omits any it can't read (omit-when-unsure) and the server drops any it
 * can't trust (a `kind` outside the taxonomy, a `filing_party_id` not in the
 * active-party set). `filed_on` is forwarded best-effort and may be non-ISO
 * (e.g. "August 14, 2023"); the upload modal normalizes it to an ISO day via
 * dateCore and only fills the picker on an unambiguous date. `case_number` /
 * `court` are auxiliary caption hints — there are no columns for them, so they
 * are shown read-only and never persisted.
 */
export interface PleadingPreviewParsed {
  kind?: string;
  filed_on?: string;
  title?: string;
  filing_party?: string;
  /** Match-only resolution to an active `case_parties` row (the picker target). */
  filing_party_id?: string;
  case_number?: string;
  court?: string;
}

export interface PleadingPreviewResponse {
  relationships?: Partial<Record<'responds_to' | 'amends', PleadingRelationshipSuggestion>>;
  relationship_preview_run_id?: string | null;
  parsed: PleadingPreviewParsed;
  warnings: string[];
  /**
   * The completed `pleading_ai_runs` id, or null when the run couldn't be made
   * confirmable (SDK/parse/finalize failure). Echo it into the eventual
   * {@link createPleading}/{@link updatePleading} as `ai_run_id` to flip the
   * saved row's provenance to 'ai_confirmed' server-side.
   */
  ai_run_id: string | null;
}

export async function previewPleading(
  caseId: string,
  text: string,
  pageCount: number,
): Promise<PleadingPreviewResponse> {
  return api.post<PleadingPreviewResponse>(
    `/cases/${caseId}/pleadings/preview`,
    { text, page_count: pageCount },
  );
}

// ─── Pleading claims — Phase 4 AI extraction (docs/pleadings-feature.md) ───
// Causes of action (complaint/cross-complaint) / affirmative defenses (answer)
// extracted by an async AI run. The wire shape is the server's DENY-BY-DEFAULT
// `_claim_to_wire` allow-list — `notes`, `extracted_payload`, `source_block_ids`,
// the raw model snapshot and `dedup_key` are NEVER serialized (internal/forensic).
//
// Trust state: an AI candidate lands `review_state='as_extracted'` /
// `metadata_source='ai_extracted'`. It is NOT confirmed and NOT live in the
// Claims Matrix until a human promotes it (deferred UI). The verbatim caption
// strings (`asserted_by_text` / `asserted_against_text`) are always present;
// the resolved party FKs are match-only and null when no confident match.

export interface PleadingClaim {
  id: string;
  /** 'cause_of_action' (complaint/cross-complaint) | 'affirmative_defense' (answer). */
  kind: string;
  /** The claim's printed label ("First", "1", "III"). Renumber-safe identity lives in dedup_key (not serialized). */
  number: string;
  title: string;
  /** VERBATIM "By …" caption text (display fallback for an unresolved party). */
  asserted_by_text: string | null;
  /** VERBATIM "Against …" caption text. */
  asserted_against_text: string | null;
  /** Match-only resolved `case_parties` uuid; null when no confident match. */
  asserted_by_party_id: string | null;
  asserted_against_party_id: string | null;
  /** VERBATIM bulleted factual allegations (markdown). Not a paraphrase. */
  allegations_md: string | null;
  /** VERBATIM statutory/contractual citations the claim rests on. */
  basis: string | null;
  /** Paragraph span as printed ("¶¶ 22-31"); null when unnumbered. */
  paragraph_range: string | null;
  source_page_start: number | null;
  source_page_end: number | null;
  /** 'as_extracted' | 'edited' | 'confirmed' | 'flagged'. AI candidates start 'as_extracted'. */
  review_state: string;
  /** 'seed' | 'human' | 'ai_extracted' | 'ai_confirmed'. */
  metadata_source: string;
  /** null | 'uncertain' | 'ambiguous_party' | 'low_confidence'. Paired with a non-blank reason. */
  extraction_flag: string | null;
  extraction_flag_reason: string | null;
  /** Sentinel 'system:claim_extractor' for AI rows; a human email after promotion. */
  extracted_by_email: string | null;
  extracted_at: string | null;
  edited_by_email: string | null;
  edited_at: string | null;
  confirmed_by_email: string | null;
  confirmed_at: string | null;
  updated_at: string;
  created_at: string;
}

/** The latest claim-extraction run's status surface (for the extracting/complete/failed UI). */
export interface PleadingClaimRun {
  id: string;
  /** 'pending' | 'running' | 'complete' | 'failed' | 'cancelled'. */
  status: string;
  claims_produced: number | null;
  error_type: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface PleadingClaimsResponse {
  /** null until the first extraction run exists for this pleading. */
  run: PleadingClaimRun | null;
  claims: PleadingClaim[];
}

/** Claim list + latest run status. Scoped to claim-bearing pleadings (404 otherwise). */
export async function fetchPleadingClaims(
  caseId: string,
  pleadingId: string,
): Promise<PleadingClaimsResponse> {
  return api.get<PleadingClaimsResponse>(
    `/cases/${caseId}/pleadings/${pleadingId}/claims`,
  );
}

/**
 * Kick off a fresh async claim extraction, preserving attorney edits (the
 * server-side conflict model: edited/confirmed claims are never overwritten,
 * rejected claims never resurrect). Resolves with the new run id (poll
 * `fetchPleadingClaims().run.status`). Throws ApiResponseError with status 409
 * when a run is already in flight, 400 when the pleading has no PDF.
 */
export async function reextractPleadingClaims(
  caseId: string,
  pleadingId: string,
  opts?: { signal?: AbortSignal },
): Promise<{ run_id: string; status: string }> {
  return api.post<{ run_id: string; status: string }>(
    `/cases/${caseId}/pleadings/${pleadingId}/re-extract`,
    {},
    { signal: opts?.signal },
  );
}

/**
 * Poll a SPECIFIC claim-extraction run by its `run_id` (#620 BE-RS, plan §9
 * R2-5). The per-complaint generate flow dispatches {@link reextractPleadingClaims}
 * then polls THIS run to a terminal state (`complete` / `failed` / `cancelled`)
 * before the scoped {@link promoteClaimsMatrix} — a successor run must not
 * displace the target (the pleading-scoped {@link fetchPleadingClaims}`.run`
 * returns only the LATEST run, so it can't be used to track one dispatch).
 */
export async function fetchPleadingClaimRun(
  caseId: string,
  pleadingId: string,
  runId: string,
  opts?: { signal?: AbortSignal },
): Promise<{ run_id: string; status: string }> {
  return api.get<{ run_id: string; status: string }>(
    `/cases/${caseId}/pleadings/${pleadingId}/claim-runs/${runId}`,
    { signal: opts?.signal },
  );
}

export type PleadingUploadPhase = 'presign' | 'upload';

/**
 * Distinguishes the two pre-create failure modes (presign API call vs the
 * S3 PUT itself) so the modal can show which phase died — an S3/network
 * failure reads very differently from an auth/validation failure.
 */
export class PleadingUploadError extends Error {
  readonly phase: PleadingUploadPhase;
  constructor(message: string, phase: PleadingUploadPhase) {
    super(message);
    this.name = 'PleadingUploadError';
    this.phase = phase;
  }
}

/**
 * Two-phase upload (spec Decision 6): presign via the API, then PUT the
 * bytes straight to S3 (plain fetch, NO auth header — it's a presigned S3
 * URL, and going direct bypasses API Gateway's payload ceiling). Returns
 * the s3_key to pass to createPleading / updatePleading.
 *
 * `onPhase` lets the caller advance its progress UI between the two phases.
 */
export async function presignPleadingUpload(
  caseId: string,
  file: File,
  onPhase?: (phase: PleadingUploadPhase) => void,
  signal?: AbortSignal,
): Promise<string> {
  signal?.throwIfAborted();
  onPhase?.('presign');
  let presigned: {
    upload_url: string;
    s3_key: string;
    expires_in: number;
    max_bytes: number;
  };
  try {
    presigned = await api.post<typeof presigned>(
      `/cases/${caseId}/pleadings/presign`,
      { filename: file.name, content_type: 'application/pdf' },
      { signal },
    );
  } catch (err) {
    signal?.throwIfAborted();
    const msg = err instanceof Error ? err.message : 'request failed';
    throw new PleadingUploadError(
      `Could not start the upload — ${msg}`,
      'presign',
    );
  }

  // Enforce the server-declared cap client-side before burning the PUT —
  // S3 would reject it anyway, but with an unhelpful XML error.
  if (presigned.max_bytes && file.size > presigned.max_bytes) {
    const mb = Math.round(presigned.max_bytes / (1024 * 1024));
    throw new PleadingUploadError(
      `PDF is too large — the limit is ${mb} MB.`,
      'presign',
    );
  }

  signal?.throwIfAborted();
  onPhase?.('upload');
  let putResp: Response;
  try {
    putResp = await fetch(presigned.upload_url, {
      method: 'PUT',
      signal,
      body: file,
      headers: { 'Content-Type': 'application/pdf' },
    });
  } catch {
    signal?.throwIfAborted();
    throw new PleadingUploadError(
      'Network error while uploading the PDF to storage.',
      'upload',
    );
  }
  if (!putResp.ok) {
    // Read the body so the error carries the actual reason (S3 returns
    // XML; the local dev shim returns JSON — both fit in 200 chars).
    const detail = await putResp.text().catch(() => '');
    const trimmed = detail.trim().replace(/\s+/g, ' ').slice(0, 200);
    throw new PleadingUploadError(
      `PDF upload failed (HTTP ${putResp.status})${trimmed ? ` — ${trimmed}` : ''}`,
      'upload',
    );
  }
  signal?.throwIfAborted();
  return presigned.s3_key;
}

// ─── Chronology (docs/chronology-feature.md, issue #487) ──────────────────
// A dated, sourced narrative of the case's events. The GET wire is an explicit
// allow-list — `source_span` / `ai_run_id` / `dedup_key` / `case_id` are
// INTERNAL and never serialized; `comment_text` IS member-facing (the Comment
// column). The external PDF export (a later phase) has its own serializer that
// drops `comment_text` too.

/** One source backing an event. Resolved sources carry exactly one FK id; an
 *  `unlinked` source is a raw free-text citation (`is_resolved=false`). */
export interface ChronologySource {
  id: string;
  source_kind: 'document' | 'pleading' | 'discovery_response' | 'unlinked';
  document_id: string | null;
  source_doc_id: string | null;
  discovery_item_id: string | null;
  discovery_item_response_id: string | null;
  is_resolved: boolean;
  /** Frozen as-cited label — what renders in the Sources column. */
  display_label: string;
  paragraph_or_page: string | null;
}

/** Precision IS granularity, independent per end. Duration (single/range/ongoing)
 *  is DERIVED from end_date / is_ongoing, never a stored field. See lib/dateCore.ts. */
export type ChronologyDatePrecision = 'day' | 'month' | 'year';
export type ChronologyEventStatus = 'committed' | 'draft' | 'rejected';

export interface ChronologyEvent {
  id: string;
  /** Full ISO YYYY-MM-DD at first-of-period (or null = undated, sorts last). */
  event_date: string | null;
  /** The start's granularity; null iff undated. */
  event_precision: ChronologyDatePrecision | null;
  /** Closed-range end (full ISO at last-of-period); null for a single/ongoing event. */
  end_date: string | null;
  /** The end's granularity; null iff no end_date. */
  end_precision: ChronologyDatePrecision | null;
  /** Open-ended range — a start with no end. */
  is_ongoing: boolean;
  /** Free-text date label override (e.g. "Spring 2024"). */
  display_date: string | null;
  event_text: string;
  /** Attorney work product — shown to case members, never exported externally. */
  comment_text: string | null;
  status: ChronologyEventStatus;
  provenance: 'ai_extracted' | 'ai_confirmed' | 'human';
  date_source: 'ai_suggested' | 'human';
  created_by_email: string;
  created_by_method: 'ai_update' | 'manual_add' | 'cross_module';
  ai_confidence: 'high' | 'medium' | 'low' | null;
  edited_by_email: string | null;
  edited_at: string | null;
  created_at: string;
  sources: ChronologySource[];
}

export interface ChronologyListResponse {
  events: ChronologyEvent[];
  status: ChronologyEventStatus;
  /** Enum the date-entry form binds to (server-owned). */
  date_precisions: ChronologyDatePrecision[];
  /** Enum the source editor binds to (server-owned). */
  source_kinds: ChronologySource['source_kind'][];
}

/** One source as the create payload expects it. A resolved source comes from
 *  the picker (carries exactly one identity FK); an `unlinked` source is a raw
 *  free-text citation (no FK). */
export interface ChronologySourceInput {
  source_kind: ChronologySource['source_kind'];
  display_label: string;
  document_id?: string;
  source_doc_id?: string;
  discovery_item_id?: string;
  discovery_item_response_id?: string;
  paragraph_or_page?: string;
}

export interface ChronologyEventCreate {
  event_date?: string | null;
  event_precision?: ChronologyDatePrecision | null;
  end_date?: string | null;
  end_precision?: ChronologyDatePrecision | null;
  is_ongoing?: boolean;
  display_date?: string | null;
  event_text: string;
  comment_text?: string | null;
  /** At least one required. */
  sources: ChronologySourceInput[];
  /** Human provenance method — 'manual_add' (in-module +Add) or 'cross_module'
   *  (the Add-to-Chronology affordance in a doc/discovery/pleading viewer).
   *  Server-validated; the AI methods are never client-settable. Default
   *  'manual_add'. provenance stays 'human', status 'committed' either way. */
  created_by_method?: 'manual_add' | 'cross_module';
}

/** PATCH edits content fields only (status/sources are later-phase flows). */
export type ChronologyEventPatch = Partial<
  Pick<
    ChronologyEventCreate,
    'event_date' | 'event_precision' | 'end_date' | 'end_precision' | 'is_ongoing'
    | 'display_date' | 'event_text' | 'comment_text'
  >
>;

/** A picker search hit. Only the three resolvable corpora are searchable
 *  (`unlinked` is the free-text escape hatch, not a corpus row). Selecting a
 *  candidate maps it to a resolved ChronologySourceInput via
 *  {@link candidateToSourceInput}. */
export interface ChronologySourceCandidate {
  source_kind: 'document' | 'pleading' | 'discovery_response';
  /** Identity FK for the kind: document_id / source_doc_id / discovery_item_id. */
  source_id: string;
  /** Discovery only: the current response row (optional refinement); else null. */
  discovery_item_response_id: string | null;
  /** Frozen as-cited label the event will store. */
  display_label: string;
  /** Dropdown-only context (Bates summary / response excerpt) — never persisted. */
  secondary: string | null;
  /** Optional family-member context for review UI — never persisted. */
  source_context?: string | null;
  /** Best-effort date string for display ordering hints — never persisted. */
  date_hint: string | null;
}

export interface ChronologySourceSearchResponse {
  results: ChronologySourceCandidate[];
  query: string;
  kind: 'all' | ChronologySourceCandidate['source_kind'];
  source_kinds: ChronologySourceCandidate['source_kind'][];
}

export async function searchChronologySources(
  caseId: string,
  q: string,
  opts: { kind?: 'all' | ChronologySourceCandidate['source_kind']; limit?: number } = {},
): Promise<ChronologySourceSearchResponse> {
  const params = new URLSearchParams({ q });
  if (opts.kind && opts.kind !== 'all') params.set('kind', opts.kind);
  if (opts.limit) params.set('limit', String(opts.limit));
  return api.get<ChronologySourceSearchResponse>(
    `/cases/${caseId}/chronology/sources/search?${params.toString()}`,
  );
}

/** Map a picker candidate to the create payload's source shape (always
 *  resolved — the picker only surfaces real corpus rows). */
export function candidateToSourceInput(
  c: ChronologySourceCandidate,
): ChronologySourceInput {
  const base: ChronologySourceInput = {
    source_kind: c.source_kind,
    display_label: c.display_label,
  };
  if (c.source_kind === 'document') base.document_id = c.source_id;
  else if (c.source_kind === 'pleading') base.source_doc_id = c.source_id;
  else if (c.source_kind === 'discovery_response') {
    base.discovery_item_id = c.source_id;
    if (c.discovery_item_response_id)
      base.discovery_item_response_id = c.discovery_item_response_id;
  }
  return base;
}

/** Map a picker candidate to a {@link ChronologySource} shape so the preview
 *  popover (SourcePeek) — read the full AI summary + Open the underlying source —
 *  can be reused on a source that has been *chosen* but not yet cited (the
 *  selected-source card in the Add-event modal). Carries the identity FK for its
 *  kind so {@link sourcePreviewRequest} + the Open descriptor resolve; `id` is a
 *  non-persisted placeholder (no citation row exists yet — the peek/open never
 *  key off it). */
export function candidateToSource(c: ChronologySourceCandidate): ChronologySource {
  return {
    id: `candidate:${c.source_kind}:${c.source_id}`,
    source_kind: c.source_kind,
    document_id: c.source_kind === 'document' ? c.source_id : null,
    source_doc_id: c.source_kind === 'pleading' ? c.source_id : null,
    discovery_item_id: c.source_kind === 'discovery_response' ? c.source_id : null,
    discovery_item_response_id: c.discovery_item_response_id,
    is_resolved: true,
    display_label: c.display_label,
    paragraph_or_page: null,
  };
}

/** The AI prefill suggestion (Shape A) for one source — date + event text the
 *  modal pre-fills. NOT a persisted event; the attorney edits and commits it
 *  through createChronologyEvent (so the committed row stays human-attributed).
 *  `date_source: 'ai_suggested'` drives the "verify the AI date" affordance. */
export interface ChronologyPrefillResult {
  event_text: string;
  event_date: string | null;
  event_precision: ChronologyDatePrecision | null;
  end_date: string | null;
  end_precision: ChronologyDatePrecision | null;
  is_ongoing: boolean;
  display_date: string | null;
  date_source: 'ai_suggested';
  model: string;
}

export async function prefillChronologyEvent(
  caseId: string,
  input: {
    source_kind: ChronologySourceCandidate['source_kind'];
    source_id: string;
    discovery_item_response_id?: string | null;
  },
): Promise<ChronologyPrefillResult> {
  return api.post<ChronologyPrefillResult>(
    `/cases/${caseId}/chronology/prefill`,
    input,
  );
}

export async function fetchChronology(
  caseId: string,
  status: ChronologyEventStatus = 'committed',
): Promise<ChronologyListResponse> {
  return api.get<ChronologyListResponse>(
    `/cases/${caseId}/chronology?status=${encodeURIComponent(status)}`,
  );
}

export async function createChronologyEvent(
  caseId: string,
  input: ChronologyEventCreate,
): Promise<ChronologyEvent> {
  return api.post<ChronologyEvent>(`/cases/${caseId}/chronology/events`, input);
}

// ── Source preview (cross-module peek) ─────────────────────────────────────
// One reusable read that resolves a source (kind + id) to a previewable
// {label, summary, open} for the click-to-peek popover shared across the
// chronology source chips AND the doc/discovery/pleading viewers. `open.mode`
// tells the FE how to open the underlying source (see openSource()).

export interface SourcePreviewRequest {
  source_kind: 'document' | 'pleading' | 'discovery_response';
  source_id: string;
  discovery_item_response_id?: string | null;
}

export type SourcePreviewOpen =
  | { mode: 'doc_viewer'; url: string }
  | { mode: 'pleading_pdf'; url: null }
  | { mode: 'discovery_item'; url: null }
  | { mode: 'none'; url: null };

export interface SourcePreview {
  source_kind: 'document' | 'pleading' | 'discovery_response';
  label: string;
  doc_type: string | null;
  summary: string | null;
  open: SourcePreviewOpen;
}

export async function fetchSourcePreview(
  caseId: string,
  req: SourcePreviewRequest,
): Promise<SourcePreview> {
  return api.post<SourcePreview>(`/cases/${caseId}/source-preview`, req);
}

/** Map a stored ChronologySource to a preview request, or null when it isn't a
 *  resolvable in-system source (an `unlinked` free-text cite, or a row missing
 *  its identity id) — the caller skips the peek fetch for those. */
export function sourcePreviewRequest(s: ChronologySource): SourcePreviewRequest | null {
  if (s.source_kind === 'document' && s.document_id) {
    return { source_kind: 'document', source_id: s.document_id };
  }
  if (s.source_kind === 'pleading' && s.source_doc_id) {
    return { source_kind: 'pleading', source_id: s.source_doc_id };
  }
  if (s.source_kind === 'discovery_response' && s.discovery_item_id) {
    return {
      source_kind: 'discovery_response',
      source_id: s.discovery_item_id,
      discovery_item_response_id: s.discovery_item_response_id ?? undefined,
    };
  }
  return null;
}

export async function updateChronologyEvent(
  caseId: string,
  eventId: string,
  patch: ChronologyEventPatch,
): Promise<ChronologyEvent> {
  return api.patch<ChronologyEvent>(
    `/cases/${caseId}/chronology/events/${eventId}`,
    patch,
  );
}

export async function deleteChronologyEvent(
  caseId: string,
  eventId: string,
): Promise<void> {
  await api.delete<void>(`/cases/${caseId}/chronology/events/${eventId}`);
}

// ─── Update runner + DRAFT-bucket disposition (P3) ────────────────────────
// "Update" re-runs AI extraction over the case corpus; candidates land as DRAFT
// events the attorney reviews per-row (incorporate → committed / reject →
// rejected + a suppression tombstone). The run executes in a background Lambda;
// the client polls getChronologyRun until terminal.

export type ChronologyRunStatus =
  | 'pending' | 'running' | 'complete' | 'failed' | 'cancelled'
  // A run that FINISHED but from an incomplete corpus (oversized/cut, output cut at
  // the token limit, or an operative pleading with no readable PDF). Distinct from a
  // clean 'complete' so an incomplete result is machine-distinguishable, not only
  // flagged in meta. Terminal + complete-like (it produced reviewable drafts).
  | 'complete_truncated';
/** What a client may SEND as corpus_mode (per_source only). */
export type ChronologyCorpusMode = 'curated' | 'all_produced';
/** What the server RECORDS + returns for a run's corpus mode. Adds
 *  'operative_pleadings' — the holistic runner's fixed corpus, which a client never
 *  SENDS but GET …/runs returns for a holistic run. */
export type ChronologyRunCorpusMode = ChronologyCorpusMode | 'operative_pleadings';
/** Which runner. 'holistic' feeds the full operative pleadings to one model call
 *  (corpus_mode does not apply); 'per_source' is the clipped-source runner scoped
 *  by corpus_mode. */
export type ChronologyRunMode = 'holistic' | 'per_source';

/** POST …/runs (202) response. `corpus_mode` is absent for a holistic run. */
export interface ChronologyRunStart {
  run_id: string;
  status: ChronologyRunStatus;
  mode?: ChronologyRunMode;
  corpus_mode?: ChronologyCorpusMode;
}

/** GET …/chronology/config — run-menu capabilities for this environment. */
export interface ChronologyConfig {
  /** true when the holistic (comprehensive) runner is enabled here — the client
   *  offers the 'Comprehensive' option only then, so it stays gated off in prod
   *  until enabled. */
  holistic_available: boolean;
}

/** GET …/runs/{rid} — status + progress. `data_sources_summary` is INTERNAL and
 *  never sent; progress is the per-status artifact COUNT map. */
export interface ChronologyRun {
  run_id: string;
  status: ChronologyRunStatus;
  corpus_mode: ChronologyRunCorpusMode;
  events_produced: number | null;
  /** {pending, done, failed, skipped} counts of the run's source artifacts. */
  artifact_counts: Record<string, number>;
  started_at: string | null;
  completed_at: string | null;
  /** A 'running' run whose heartbeat is stale (likely crashed). */
  stale: boolean;
  /** true when the corpus was too large and was cut to the char budget — the
   *  resulting chronology is built from INCOMPLETE text. Only the flag is exposed,
   *  never the corpus composition. Absent/false on older or per-source runs. */
  corpus_truncated?: boolean;
}

const TERMINAL_RUN_STATUSES: ReadonlySet<ChronologyRunStatus> = new Set([
  'complete', 'complete_truncated', 'failed', 'cancelled',
]);

export function isChronologyRunTerminal(s: ChronologyRunStatus): boolean {
  return TERMINAL_RUN_STATUSES.has(s);
}

export interface StartChronologyRunOpts {
  mode: ChronologyRunMode;
  /** Only meaningful (and only sent) for the per-source runner. */
  corpusMode?: ChronologyCorpusMode;
}

export async function startChronologyRun(
  caseId: string,
  opts: StartChronologyRunOpts,
): Promise<ChronologyRunStart> {
  return api.post<ChronologyRunStart>(`/cases/${caseId}/chronology/runs`, {
    mode: opts.mode,
    // corpus_mode only applies to per_source; the holistic runner ignores it.
    ...(opts.mode === 'per_source' && opts.corpusMode
      ? { corpus_mode: opts.corpusMode }
      : {}),
  });
}

export async function getChronologyConfig(caseId: string): Promise<ChronologyConfig> {
  return api.get<ChronologyConfig>(`/cases/${caseId}/chronology/config`);
}

export async function getChronologyRun(
  caseId: string,
  runId: string,
): Promise<ChronologyRun> {
  return api.get<ChronologyRun>(`/cases/${caseId}/chronology/runs/${runId}`);
}

export async function cancelChronologyRun(
  caseId: string,
  runId: string,
): Promise<{ run_id: string; status: ChronologyRunStatus }> {
  return api.post<{ run_id: string; status: ChronologyRunStatus }>(
    `/cases/${caseId}/chronology/runs/${runId}/cancel`,
    {},
  );
}

/** DRAFT → committed (a human ratifies the AI suggestion). Returns the event. */
export async function incorporateChronologyEvent(
  caseId: string,
  eventId: string,
): Promise<ChronologyEvent> {
  return api.post<ChronologyEvent>(
    `/cases/${caseId}/chronology/events/${eventId}/incorporate`,
    {},
  );
}

/** DRAFT → rejected (+ a suppression tombstone server-side). */
export async function rejectChronologyEvent(
  caseId: string,
  eventId: string,
): Promise<{ id: string; status: ChronologyEventStatus }> {
  return api.post<{ id: string; status: ChronologyEventStatus }>(
    `/cases/${caseId}/chronology/events/${eventId}/reject`,
    {},
  );
}

// ─── Claims Matrix (docs/claims-matrix-feature.md, issue #564) ─────────────
// The human-promotion + evidence-curation layer over the claims the Pleadings
// Phase-4 extractor produces and quarantines. The GET wire is the deny-by-default
// allow-list from api/routes/claims_matrix.py: `document_id` / `s3_key` / raw AI
// summary / `source_block_ids` / `extracted_payload` / run ids / raw
// `discovery_item_id` NEVER serialize — including inside `open_target`. Evidence
// attaches at ALLEGATION grain (not COA grain); each evidence cell is per
// PERSPECTIVE (plaintiff / defendant). Provenance language is EDITOR-AGNOSTIC
// (Decision 17): "Manually written" / "AI-suggested" / "AI-suggested · has been
// revised" — never "attorney".

export type MatrixPerspective = 'plaintiff' | 'defendant';
/** Two orthogonal trust axes (§4a). review_state = lifecycle; metadata_source =
 *  sticky origin (never cleared once AI-originated). */
export type MatrixCellReviewState = 'as_extracted' | 'edited' | 'confirmed';
export type MatrixCellOrigin = 'human' | 'ai_extracted' | 'ai_confirmed';
export type MatrixAllegationReviewState =
  | 'as_extracted' | 'edited' | 'confirmed' | 'flagged' | 'withdrawn';

/** L0 cross-version lineage (mig 086). How this allegation's wording relates to its
 *  predecessor across operative pleadings. `match_confidence` is NEVER on the wire. */
export type MatrixChangeClassification =
  | 'reworded' | 'new' | 'withdrawn' | 'carried';

/** L2 reconciliation state (D3). `revised` is NON-terminal — it flips to `reconciled`
 *  on the first evidence edit; the Finish gate treats it as unresolved. */
export type MatrixReconcileStatus =
  | 'pending' | 'confirm_fits' | 'revised' | 'reconciled' | 'archived' | 'n/a';

/** A source link on an evidence cell — the matrix-specific wire shape (NOT the
 *  chronology source). The ONLY id is `open_target.source_link_id` (the
 *  claim_evidence_sources ROW id — matrix-internal, never a document/discovery
 *  id). Opening resolves the link server-side via source-preview. */
export interface MatrixEvidenceSource {
  id: string;
  source_kind: 'document' | 'discovery_response';
  display_label: string;
  page_or_paragraph: string | null;
  open_target: {
    mode: 'doc_viewer' | 'discovery_item' | 'none';
    source_link_id: string;
  };
}

/** One evidence entry under an allegation, scoped to a perspective. */
export interface MatrixEvidenceCell {
  id: string;
  allegation_id: string;
  perspective: MatrixPerspective;
  ordering: number;
  text_md: string;
  review_state: MatrixCellReviewState;
  metadata_source: MatrixCellOrigin;
  created_by_email: string;
  created_at: string;
  edited_by_email: string | null;
  edited_at: string | null;
  confirmed_by_email: string | null;
  confirmed_at: string | null;
  sources: MatrixEvidenceSource[];
}

/** An allegation row under a COA. text_original (the revert target) is INTERNAL
 *  and never on the wire — revert is a server action. */
export interface MatrixAllegation {
  id: string;
  ordinal: number;
  text_md: string;
  paragraph_cite: string | null;
  is_incorporation: boolean;
  is_placeholder: boolean;
  /**
   * A5: server-derived `text_md != text_original`. text_original itself stays OFF
   * the wire; this boolean is the only drift signal. When true, the card shows
   * "edited — differs from filed text" and NEVER the "as pleaded" lock.
   */
  is_edited_from_pleading: boolean;
  review_state: MatrixAllegationReviewState;
  edited_by_email: string | null;
  edited_at: string | null;
  reviewed_empty_at: string | null;
  reviewed_empty_by_email: string | null;
  created_at: string;
  updated_at: string;
  /**
   * L0/L2 lineage (mig 086) — null when the allegation has no cross-version lineage.
   * `change_classification` drives the per-card amendment badge; `reconcile_status`
   * drives the Confirm/Revise affordance (a `revised` card shows an "edit owed" state,
   * not a resolved checkmark). `lineage_key` is the chain id for the wording-history
   * /versions lookup. `match_confidence` is server-internal and never present here.
   */
  change_classification: MatrixChangeClassification | null;
  reconcile_status: MatrixReconcileStatus | null;
  lineage_key: string | null;
  /**
   * L1: how many promoted versions share this allegation's lineage chain. A
   * member-safe COUNT (never an id / match_confidence) so the list card can render
   * the mockup's "Wording history · N filings" toggle and gate it precisely on
   * `version_count >= 2` — no per-card /versions fetch. 0 when there is no lineage.
   */
  version_count: number;
  /** Evidence keyed by perspective; both perspectives always returned. */
  evidence: Record<MatrixPerspective, MatrixEvidenceCell[]>;
}

/** A cause of action (COA) banner with its allegations. */
export interface MatrixCoa {
  id: string;
  kind: string;
  number: string | null;
  title: string;
  asserted_by_text: string | null;
  asserted_against_text: string | null;
  basis: string | null;
  paragraph_range: string | null;
  metadata_source: string;
  review_state: string;
  /** A6: who promoted/confirmed this COA — server-set, member-safe. */
  confirmed_by_email: string | null;
  confirmed_at: string | null;
  created_at: string;
  allegations: MatrixAllegation[];
}

/** Per-perspective coverage rollup (Decision 9), excluding is_incorporation. */
export interface MatrixCoverage {
  not_sourced: number;
  ai_draft: number;
  confirmed: number;
  reviewed_empty: number;
  /**
   * A3: whole-COA `is_placeholder` stubs — a missing-STRUCTURE gap (route to
   * Pleadings), counted in their OWN bucket, never as confirmed/ai_draft/not_sourced.
   */
  placeholder: number;
}

/** The promoted-pleading version stamp (A11 anchor). */
export interface MatrixReflectedPleading {
  id: string;
  kind: string | null;
  title: string | null;
  filed_on: string | null;
  is_operative: boolean | null;
}

/** A withdrawn allegation surfaced for acknowledgement (F6). Withdrawn allegations have
 *  no row in the live grid, so this is the only place they are reachable for archive. */
export interface MatrixWithdrawnEntry {
  allegation_id: string;
  coa_title: string;
  text: string;
  reconcile_status: MatrixReconcileStatus;
}

/** L2 amendment / reconciliation summary (D6), null when there is no unreconciled
 *  lineage. `resolved`/`total` drive the Finish gate; a `revised` row is NOT resolved. */
export interface MatrixAmendment {
  label: string;
  short: string | null;
  prior_short: string | null;
  filed_on: string | null;
  counts: { reworded: number; new: number; withdrawn: number };
  withdrawn: MatrixWithdrawnEntry[];
  resolved: number;
  total: number;
}

export interface ClaimsMatrix {
  represents_side: MatrixPerspective | null;
  /**
   * The complaint the user OPENED — the operative scope anchor resolved from the
   * `?complaint=<source_doc_id>` param (#620, plan §8 M1). DISTINCT from
   * `reflected_pleading`: `selected_complaint` is the tile the reviewer clicked
   * (the scope), whereas `reflected_pleading` is the version whose claims were
   * actually promoted into this scope. The two differ when the promoted version is
   * not the one the user is viewing (e.g. an unpromoted operative successor over a
   * promoted predecessor). Null only on the retired case-wide read.
   */
  selected_complaint: MatrixReflectedPleading | null;
  reflected_pleading: MatrixReflectedPleading | null;
  /** A newer is_operative pleading differs from the reflected one (staleness),
   *  evaluated WITHIN the selected complaint's scope (#620). */
  newer_unreconciled_pleading: boolean;
  /** L2 amendment summary (mig 086); null when no unreconciled lineage. */
  amendment: MatrixAmendment | null;
  coas: MatrixCoa[];
  coverage: Record<MatrixPerspective, MatrixCoverage>;
}

// ─── #620 — per-operative-complaint breakout (docs/claims-breakout-CONTRACT.md) ──
// The Claims Matrix is being restructured from one-matrix-per-case to
// N-matrices-per-operative-complaint. `/index` is the new tile-picker landing;
// the GET + promote below are now SCOPED to one operative complaint's version
// chain via its `source_doc_id`. See docs/plan-620-claims-breakout.md §2.

/** One operative complaint / cross-complaint tile in the picker (plan §2.3).
 *  The server-enforced set is `is_operative && thread_kind ∈ {complaint,
 *  cross_complaint}` — answers are NEVER surfaced (#618). One row per operative
 *  scope, so multiple cross-complaints by different filing parties are distinct
 *  rows. `source_doc_id` is the scope handle passed back to the scoped GET /
 *  promote (it is the already-FE-visible pleading id, so no new id leaks). */
export interface ClaimsMatrixComplaintTile {
  /** The operative pleading's `source_doc_id` — the scope anchor for this matrix. */
  source_doc_id: string;
  title: string;
  /** snake_case kind from the server taxonomy (e.g. `complaint`, `amended_complaint`,
   *  `cross_complaint`). */
  kind: string;
  /** Operative-thread family — always `complaint` or `cross_complaint` on a tile
   *  (the index filters to those two; never null here). */
  thread_kind: string;
  /** Canonical filing party (display fallback for compound/off-roster filers). */
  filing_party: string | null;
  /** Best-effort server-side party resolution; disambiguates cross-complaints. */
  filing_party_id: string | null;
  /** YYYY-MM-DD; null only on legacy rows (sorts last). */
  filed_on: string | null;
  is_operative: boolean;
  /** Count of CONFIRMED causes of action in this scope (plan §2.3). Drives the
   *  "{coa_count} causes of action" status line. */
  coa_count: number;
  /** `coa_count > 0` — whether a matrix has been generated (promoted) for this
   *  complaint. False ⇒ the tile reads "Not yet generated". */
  generated: boolean;
}

/** The tile-picker payload for `GET /cases/{id}/claims-matrix/index` (plan §2.3). */
export interface ClaimsMatrixIndex {
  complaints: ClaimsMatrixComplaintTile[];
}

/** One version of an allegation in its wording-history chain (L1, GET /versions). */
export interface MatrixAllegationVersion {
  short: string | null;
  filed_on: string | null;
  text: string;
  /** This version's pleading is the thread's current operative (derived, not a filter). */
  is_current: boolean;
}

/** Tile-picker data (#620): one entry per operative complaint / cross-complaint,
 *  with a per-scope CONFIRMED cause-of-action count. Answers are excluded
 *  server-side (#618). See docs/claims-breakout-CONTRACT.md §3. */
export async function fetchClaimsMatrixIndex(
  caseId: string,
): Promise<ClaimsMatrixIndex> {
  return api.get<ClaimsMatrixIndex>(`/cases/${caseId}/claims-matrix/index`);
}

/**
 * The promoted matrix SCOPED to one operative complaint (#620). `complaintId` is
 * the operative pleading's `source_doc_id`; the server resolves it to the scope's
 * version chain and returns only that scope's confirmed causes of action. REQUIRED
 * — the case-wide read is retired (the server 400s on a missing `complaint`, 404s
 * on a non-operative / inaccessible scope). See docs/claims-breakout-CONTRACT.md §1.
 */
export async function fetchClaimsMatrix(
  caseId: string,
  complaintId: string,
  opts?: { signal?: AbortSignal },
): Promise<ClaimsMatrix> {
  const qs = new URLSearchParams({ complaint: complaintId });
  return api.get<ClaimsMatrix>(`/cases/${caseId}/claims-matrix?${qs.toString()}`, { signal: opts?.signal });
}

// ── Claims Matrix v2 — Cause of Action → Contention → Evidence (#1093) ────────
// The new model's read types. They live alongside the allegation-based ones
// above rather than replacing them: the old screen stays the nav destination and
// the rollback target for the whole gated phase (#1102), and #1107 removes both
// it and its types once the flip is judged a success.

/** A party on a pleading. `is_client` is the ONLY ownership signal (plan D8) —
 *  it decides whose words a contention is, and therefore which column could ever
 *  be editable. It says nothing about which *user* may type there; that is a role
 *  check every authoring ticket owes separately (epic #1109). */
export interface MatrixParty {
  id: string;
  name: string;
  short_name: string | null;
  is_client: boolean;
}

export type MatrixSide = 'asserting' | 'responding';

export type MatrixReviewState = 'as_extracted' | 'edited' | 'confirmed' | 'flagged';

/** D9 — `argument` is an opening-statement cite and is NOT evidence. It has its
 *  own kind precisely so the UI can flag it. */
export type MatrixEvidenceKind =
  | 'document' | 'discovery_response' | 'pleading' | 'testimony'
  | 'declaration' | 'public_record' | 'authority' | 'argument';

export interface MatrixEvidence {
  id: string;
  position: number;
  source_kind: MatrixEvidenceKind;
  cite: string;
  quote: string | null;
  support_md: string | null;
  /** Derived server-side from `support_md <> support_original`. */
  edited: boolean;
  note: string | null;
  locator: {
    exhibit_label: string | null;
    bates: string | null;
    page_start: number | null;
    page_end: number | null;
    line_start: number | null;
    line_end: number | null;
    para: string | null;
  };
  objections_only: boolean;
  review_state: MatrixReviewState;
  extraction_flag_reason: string | null;
  /** A DIFFERENT assertion from `review_state`: that a lawyer opened the cited
   *  document and confirmed it says what the support sentence claims. Never
   *  inferred from authorship — #1098 sets it. */
  verified_state: 'unverified' | 'verified' | 'flagged';
  verified_by_email: string | null;
  verified_at: string | null;
  verification_flag_reason: string | null;
}

export interface MatrixContention {
  id: string;
  /** Relative to the PLEADING, not to the claim it sits under (plan D4). On a
   *  defence the display columns swap; the stored side does not. */
  side: MatrixSide;
  seq: number;
  text_md: string;
  /** What the row said before anyone edited it — immutable server-side. Here
   *  because **revert is an ordinary edit** that sends this back (#1097): with
   *  no endpoint of its own, the screen has to be holding the original.
   *
   *  Optional because the dev mock only has it from a row's first edit (five of
   *  its six seeds omit it); the API always sends it. Guard on it before
   *  offering revert — and do not make it required without fixing the seeds,
   *  which is what the optionality is really protecting. */
  text_original?: string;
  edited: boolean;
  review_state: MatrixReviewState;
  extraction_flag_reason: string | null;
  /** The document stopped producing this proposition, so a re-extract stood it
   *  down instead of deleting it — it may carry evidence and a filed citation
   *  number. ORTHOGONAL to `review_state`, which keeps the value it had: a
   *  `confirmed` contention that is retired is a confirmed proposition the
   *  pleading no longer asserts, and both halves matter. */
  retired: boolean;
  /** Why, e.g. "no longer produced by Answer of Braddock, filed 14 Mar". Always
   *  present when `retired` is true — the schema refuses the pair apart. */
  retired_reason: string | null;
  /** Who stood behind this row, and when. On a contention a person TYPED it
   *  lands `confirmed` by its own author (D12), so the name is just the author
   *  and says nothing. It only carries information when the row was EXTRACTED
   *  and confirmed afterwards — a human checking a machine's reading. */
  confirmed_by_email: string | null;
  confirmed_at: string | null;
  /** Whether this is our client's position — the single fact affordances hang
   *  off. Derived from the origin party's `is_client`, NOT from `side`: our
   *  client and a co-defendant we do not represent are both `responding` on the
   *  same count. Fails closed — an unresolved party is never ours. */
  is_ours: boolean;
  origin_party: { id: string; name: string | null; short_name: string | null } | null;
  /** Provenance as a rendered label ("Braddock Answer, filed 2026-01-23"); the
   *  source document id never crosses the wire. */
  source: string | null;
  /** This contention also sits under another count. Shown on EVERY copy — the
   *  old screen showed it nowhere, so the first sign a user got was deleting it
   *  in one place and watching it vanish from another. */
  shared: boolean;
  evidence: MatrixEvidence[];
}

export type MatrixDisposition =
  | 'live' | 'demurrer_sustained' | 'dismissed' | 'withdrawn' | 'adjudicated';

export interface MatrixClaim {
  id: string;
  /** TEXT on the wire, not a number. A pleading numbers its counts "1", "2",
   *  "10", and sometimes "2(a)" or "First" — `case_claims.number` is `text NOT
   *  NULL`. Typing it as a number invites arithmetic or `>` comparisons that
   *  would be wrong on every one of those. */
  number: string;
  title: string | null;
  basis: string | null;
  review_state: MatrixReviewState;
  extraction_flag_reason: string | null;
  /** The court's **ruling**, separate from `review_state` (plan D7 as amended).
   *  Distinct from `disposition_proposed`, which extraction writes and which
   *  the server deliberately does not send: an unconfirmed reading must not
   *  strike a count through on screen or in an export. Only a person copies a
   *  proposal across, and there is no setter in the product yet (#1101). */
  disposition: MatrixDisposition;
  disposition_note: string | null;
  /** The document the ruling was transcribed from, as a label. */
  disposition_source: string | null;
  contentions: Record<MatrixSide, MatrixContention[]>;
  /** Recorded claim and linked-contention filings, including when no contention
   *  rows are displayed. Absent on older cached/mock responses. */
  sources?: MatrixFiling[];
  /** Defences only. Two defendants pleading the same defence are two rows, and
   *  the rows are indistinguishable without it (plan D6). A list, not one party:
   *  migration 108 models claim parties as `(party_id, relation)` sets, and a
   *  jointly filed answer names several. */
  asserted_by?: MatrixParty[];
  /** Causes of action only — the parties THIS count is pleaded against, which
   *  heads its responding column. Not the pleading's responding group: a count
   *  naming one of three defendants must not be shown as against all three. */
  asserted_against?: MatrixParty[];
}

/** The seven issues `claims_matrix_setup_items()` can report. Each keys off a
 *  DIFFERENT table, and `ref` is a bare uuid — so the issue name is the only
 *  thing that says what `ref` points at. */
export type MatrixSetupIssue =
  | 'pleading_missing_side'
  | 'answer_without_pleading'
  | 'no_client_party'
  | 'client_parties_unconfirmed'
  | 'defense_without_asserting_party'
  | 'matter_details_unconfirmed'
  | 'pleadings_share_a_filer';

/** One outstanding setup item from `claims_matrix_setup_items()` (#1105).
 *
 *  `ref` points at a different table per issue — `case_pleadings` for
 *  `pleading_missing_side` / `pleadings_share_a_filer`, `case_source_docs` for
 *  `answer_without_pleading`, `case_claims` for
 *  `defense_without_asserting_party`, `cases` for the two roster issues. Never
 *  hand `ref` straight to a link: `label` and `source_doc_id` are resolved
 *  server-side precisely so the screen does not have to know which is which. */
export interface MatrixSetupItem {
  issue: MatrixSetupIssue | string;
  ref: string;
  detail: string | null;
  /** The thing that needs fixing, named — "2AC, filed 2025-11-12". Null when it
   *  has no operative version to name it by. */
  label: string | null;
  /** The `case_source_docs.id` to open in the Pleadings Edit modal (#1104), and
   *  ONLY for the two relationship repairs that modal owns. Null means the
   *  repair is in this panel — do not offer a link. */
  source_doc_id: string | null;
  /** How widely this item blocks: `null` for every pleading in the case, a
   *  `case_pleadings.id` for only that one.
   *
   *  The matrix read FILTERS on it (#1094), so an item scoped to another
   *  pleading does not reach you and does not withhold the matrix you opened.
   *  That is why the picker exists in the shape it does: with the gate
   *  narrowed, a clean pleading opens straight to its matrix and the setup
   *  panel — the only editor for these — never renders, so something has to
   *  list the pleadings still carrying items. The picker's per-tile
   *  `setup_needed` is that signal. */
    scope_pleading_id: string | null;
  /** What the pleading itself says about the answer this item is asking for
   *  (#1229), or null when it says nothing and for every issue that has no
   *  verbatim source to cite.
   *
   *  **Evidence to read, not an answer to adopt.** The panel renders it beside
   *  the control; it never pre-selects from it. Pre-ticking needs the
   *  multi-party caption split (#1095/#1186) and is the half that can go wrong
   *  silently, so it is a follow-up — citing a source a person reads is
   *  strictly safer than pre-selecting one they may not. */
  evidence: MatrixSetupEvidence | null;
}

/** Common to both evidence shapes: the document the text was read out of, and
 *  whether anyone has confirmed that reading.
 *
 *  `derived` and `doc_metadata_confirmed` are the guard against laundering an
 *  extraction into a confirmation. The caption text is extractor output and
 *  `case_source_docs` has its own `metadata_confirmed_at` gate, so evidence
 *  whose own reading is unconfirmed has to say so rather than render as
 *  established fact. */
interface MatrixSetupEvidenceDoc {
  /** The answer or pleading this was read out of, cited by NAME.
   *
   *  No id and no link, deliberately. #1229 asked for an "open the document"
   *  affordance and there is no surface to open: `?edit=` is #1104's
   *  Upload/Edit form, which `source_doc_id`'s own doc-comment already says is
   *  the wrong place to send an in-panel item. The caption is the evidence and
   *  reads fine on its own; a link can be added when a reader exists. */
  doc_label: string | null;
  /** The filer, named as the pleading names it. */
  doc_filing_party: string | null;
  doc_metadata_confirmed: boolean;
}

/** Who the answer says raised this affirmative defence, verbatim. */
export interface MatrixDefenceEvidence extends MatrixSetupEvidenceDoc {
  kind: 'defence_raised_by';
  /** `case_claims.asserted_by_text` — the caption's own words, e.g. "Quillon
   *  Cold Chain Holdings, LLC and Sabine Quillon".
   *
   *  NULL when the defence carries no "(By X)" line of its own, which is the
   *  ORDINARY answer: the filer is stated once, in the caption and the
   *  signature block. This used to be `string` and the comment used to promise
   *  "never empty, the server sends no evidence block at all" — true until the
   *  server gained the filing-party fallback, after which a null reached
   *  `normalizeName` and took the whole screen down with
   *  `null is not an object (evaluating 's.toLowerCase')`. */
  asserted_by_text: string | null;
  /** Which source produced `suggested_party_ids`. A pre-tick has to arrive
   *  beside the thing it came from, or the reader cannot check it — and the
   *  caption-mismatch warning is meaningless against `filing_party`, because
   *  there is no caption line for a ticked party to be absent from. */
  suggested_from: 'defence_caption' | 'filing_party';
  /** True when this text is extractor output rather than a human's entry. */
  derived: boolean;
  /** The parties the chosen source NAMES, resolved server-side. Pre-ticked.
   *  A suggestion to confirm against the quote above it, never a decision —
   *  every box stays untickable and nothing is written until you click. */
  suggested_party_ids: string[];
}

/** The `(By … Against …)` line of each count, which is the only record anywhere
 *  of a cross-complaint's responding side. */
export interface MatrixSidesEvidence extends MatrixSetupEvidenceDoc {
  kind: 'pleading_sides';
  counts: {
    number: string | null;
    title: string | null;
    asserted_by_text: string | null;
    asserted_against_text: string | null;
    derived: boolean;
  }[];
  /** Per side, the parties EVERY count agrees on — an intersection, not a
   *  union. The card writes one pair of sides for the whole pleading, so
   *  pre-ticking a party only some counts name would state something the
   *  pleading does not. Divergent counts therefore suggest less, and the
   *  warning beside them says to check the rest. */
  suggested_asserting_party_ids: string[];
  suggested_responding_party_ids: string[];
}

export type MatrixSetupEvidence = MatrixDefenceEvidence | MatrixSidesEvidence;

export interface ClaimsMatrixV2 {
  claims_matrix_v2_enabled_at: string | null;
  /** Non-empty ⇒ the matrix is withheld and the setup panel renders instead.
   *  Every item makes some column silently wrong rather than visibly empty. */
  setup_needed: MatrixSetupItem[];
  pleading: {
    id: string;
    kind: 'complaint' | 'cross_complaint';
    title: string | null;
    short_title: string | null;
    filed_on: string | null;
    /** Actual operative upload, distinct from the stable pleading id above. */
    filing?: MatrixFiling | null;
    /** The operative version's claim extraction — the asserting column's
     *  indicator. Present even when `setup_needed` is non-empty, because the
     *  setup panel is where a user sits during a first upload. */
    extraction: MatrixExtraction;
  };
  parties?: Record<MatrixSide, MatrixParty[]>;
  /** Which side of THIS pleading our client is on — derived from `is_client`,
   *  never configured. `null` means no client party, and nothing is ours. */
  our_side?: MatrixSide | null;
  causes_of_action?: MatrixClaim[];
  affirmative_defenses?: MatrixClaim[];
  /** The answers filed to this pleading, one row per answering party (#1095).
   *
   *  This is what the responding column of a count renders when it is empty,
   *  and it is empty for a reason rather than by failure: under CCP § 431.30 a
   *  general denial is available against an unverified complaint, so the
   *  ordinary answer denies the allegations as a whole and asserts nothing
   *  count by count. Its substance is the affirmative defences (their own tab)
   *  and, later, its responses to contention interrogatories.
   *
   *  So the column needs a FOURTH state beyond the mock's three — "read, and
   *  correctly empty" — or "extracted" is indistinguishable from "failed",
   *  which is the legacy screen's "Not yet generated" lie rebuilt. */
  responses_on_file?: MatrixResponseOnFile[];
}

/**
 * One document's claim-extraction state, per column (#1095).
 *
 * Extraction takes ~2 minutes and runs automatically on upload, so without this
 * the screen is silent for two minutes and then shows a responding column that
 * is legitimately empty — indistinguishable from a failure.
 *
 * `stalled` is #580 and is deliberately its own state: a run died 32 seconds in,
 * its heartbeat never advanced, `status` stayed `running` for three days with no
 * error, and the screen reported success. A dead run must render as neither
 * finished nor in-flight. The server decides it by silence against the 900s
 * Lambda ceiling, not against the 1h reaper or 24h terminaliser windows — those
 * must outlive a live run, so keying on them would keep a spinner turning for an
 * hour after the process died.
 */
export interface MatrixExtraction {
  state: 'never' | 'running' | 'stalled' | 'failed' | 'complete';
  started_at: string | null;
  /** When the document was last read SUCCESSFULLY. Survives a failed retry, so
   *  "read 26 Sep" does not blank out while the rows it produced are on screen. */
  read_at: string | null;
  /** Only on `failed`; cleared once a re-run succeeds. */
  error_type: string | null;
  /** Whether contention extraction was switched on for this case when the run
   *  went, from the `CLAIMS_MATRIX_V2_EXTRACT_CASES` allow-list.
   *
   *  ORTHOGONAL to `state`, deliberately not a sixth value of it: a run can be
   *  running, complete or failed while the case is or is not allow-listed, and
   *  folding them would force a choice between two facts that are both true.
   *
   *  It exists because `complete` on a case that is NOT allow-listed is the most
   *  misleading thing this screen can say — the run dispatches, succeeds, writes
   *  its counts and produces no contentions, and "the filing was read and
   *  produced no contentions" is then true of the run and false about the
   *  document. The server computed and sent this from the start; it was missing
   *  from this type, which is most of why nothing consumed it. */
  contentions_enabled: boolean;
}

/** A source filing with its own run state and an opaque re-read target. */
export interface MatrixFiling {
  id: string;
  label: string | null;
  filed_on: string | null;
  can_reextract: boolean;
  extraction: MatrixExtraction;
}

/** One answer on file against the pleading, and what its denial says. */
export interface MatrixResponseOnFile {
  /** `case_source_docs.id` — a navigation target (open it in Pleadings), not a
   *  provenance label. Labels are resolved server-side. */
  id: string;
  /** "Answer of Braddock Logistics, filed 2026-01-23". */
  label: string | null;
  filed_on: string | null;
  is_operative: boolean;
  filing_party_name: string | null;
  filing_party_short_name: string | null;
  filing_party_is_client: boolean;
  /** `general` is the ordinary case and the one worth saying out loud: it tells
   *  a reader every element is still in issue.
   *
   *  `null` is OUR ignorance — nobody has read this document — and must never
   *  render as "denies nothing". That is what `'none'` means. */
  denial_posture: 'general' | 'specific' | 'mixed' | 'none' | null;
  /** Complaint paragraphs this answer ADMITS, counted. Paragraphs, not counts:
   *  an answer responds to numbered allegations, and the paragraph → count
   *  mapping is nobody's job yet. Admissions are judicial admissions, so they
   *  are worth keeping before anything reads them. */
  admitted_paragraph_count: number;
  /** Whether the document has been read, as distinct from what it says. Drives
   *  "not yet read" versus "generally denied". */
  denial_read: boolean;
  extraction: MatrixExtraction;
  can_reextract?: boolean;
}

/**
 * One pleading's matrix under the new model (#1093). `pleadingId` is a
 * `case_pleadings.id` — a pleading, not a document: it spans every amendment, so
 * a contention written against the 2AC's second count is still there in the 4AC.
 *
 * The server branches on the presence of `?pleading`; `fetchClaimsMatrix` above
 * sends `?complaint` and reaches the old allegation-based reader on the same
 * path. Both live until #1107.
 */
export async function fetchClaimsMatrixV2(
  caseId: string,
  pleadingId: string,
  opts?: { signal?: AbortSignal },
): Promise<ClaimsMatrixV2> {
  const qs = new URLSearchParams({ pleading: pleadingId });
  return api.get<ClaimsMatrixV2>(
    `/cases/${caseId}/claims-matrix?${qs.toString()}`, { signal: opts?.signal });
}

// ── #1094, the pleading picker ─────────────────────────────────────────────

/** One tile on the new Claims Matrix landing page (#1094).
 *
 *  `id` is a **`case_pleadings.id`**, and that is the point of the ticket's id
 *  change: it spans every amendment, so a bookmarked `?pleading=` survives a
 *  new amendment being flipped operative. The old picker keyed tiles on the
 *  operative version's `source_doc_id`, which moves. Do not confuse it with the
 *  `pleading_id` in `pleadings.ts` / the Pleadings module, which is a
 *  `case_source_docs.id` — one *version*. */
export interface ClaimsMatrixV2Tile {
  id: string;
  kind: 'complaint' | 'cross_complaint';
  /** The operative version's caption. All null when the pleading has no
   *  operative version — a real state, and the tile still renders (see
   *  `tileTitle` and `has_operative_version`). */
  title: string | null;
  short_title: string | null;
  filed_on: string | null;
  extraction?: MatrixExtraction;
  /** False when no version of this pleading is flagged operative, which is why
   *  the caption above is empty. Worth saying on the tile: without it a
   *  pleading in that state is named only by its kind ("Complaint") and is
   *  indistinguishable from the real one beside it. */
  has_operative_version: boolean;
  /** The current version's free-text filer, for the tile subtitle to fall back
   *  on. `parties.asserting` is the authority and can name several; this is one
   *  field off one document, and it is what the tile says while the authority
   *  is empty — which is every pleading until somebody fills the sides in
   *  through the setup panel. */
  filing_party: string | null;
  parties: Record<MatrixSide, MatrixParty[]>;
  /** Which side of THIS pleading our client is on, derived from `is_client`.
   *  `null` is a legitimate state, NOT a reason to assume "we respond" — on a
   *  complaint we filed, that would be a false statement about the matter. */
  our_side: MatrixSide | null;
  /** Both kinds, because a pleading with 8 counts and 19 defences advertising
   *  "8" is the bug this ticket exists to fix. Counted over everything but
   *  `superseded`, the same predicate the matrix read uses, so a tile can never
   *  advertise a different number from the rows behind it. */
  cause_of_action_count: number;
  affirmative_defense_count: number;
  /** How many of the above the matrix renders struck through — disposed by a
   *  demurrer, dismissal, withdrawal or adjudication. The totals still include
   *  them; this says how many are no longer live, which on a pleading that has
   *  survived a demurrer is usually the most interesting thing about it. */
  cause_of_action_struck: number;
  affirmative_defense_struck: number;
  /** An item scoped to THIS pleading is outstanding, so its matrix will not
   *  open. Mirrors the gate the matrix read now applies (#1094 narrowed it on
   *  `scope_pleading_id`), so a badged tile and a refused matrix cannot
   *  disagree.
   *
   *  A clear tile is only reachable if the case also has no case-wide items —
   *  those block every pleading and appear in `setup_needed` above with a null
   *  scope. The screen has to read both. */
  setup_needed: boolean;
}

export interface ClaimsMatrixV2Index {
  claims_matrix_v2_enabled_at: string | null;
  /** Exactly the items `ClaimsMatrixV2.setup_needed` carries — same shape, same
   *  `scope_pleading_id`, from the same `_setup_items`. Case-wide here rather
   *  than scoped to one pleading, so this is the only place the whole list is
   *  visible: the matrix read now filters to what blocks the pleading you
   *  opened.
   *
   *  Non-empty does NOT mean nothing opens. An item with a non-null
   *  `scope_pleading_id` blocks only that pleading — which is what the tile's
   *  own `setup_needed` reflects. A null scope blocks every pleading. */
  setup_needed: MatrixSetupItem[];
  pleadings: ClaimsMatrixV2Tile[];
}

/**
 * The pleading picker's tiles (#1094) — the landing page of the new Claims
 * Matrix, since a matrix belongs to one pleading (epic #1109 rule 3).
 *
 * A route of its own rather than a param on `/claims-matrix/index`, because the
 * picker is the **no-param** arm of the Claims Matrix route: unlike the two
 * matrices, the two pickers have nothing on the URL to branch on. The router
 * branches on `cases.claims_matrix_v2_enabled_at` and each arm calls its own
 * endpoint — which also leaves `claims_matrix.py` untouched for #1107.
 *
 * Already sorted by the pleading's first-filed version. Do not re-sort on
 * `filed_on`: that is the *operative* version's date, so an uploaded amendment
 * would reorder the grid.
 */
export async function fetchClaimsMatrixV2Index(
  caseId: string,
  opts?: { signal?: AbortSignal },
): Promise<ClaimsMatrixV2Index> {
  return api.get<ClaimsMatrixV2Index>(
    `/cases/${caseId}/claims-matrix/pleadings`, { signal: opts?.signal });
}

// ── #1105, the per-case setup panel ────────────────────────────────────────
//
// `setup_needed` above says WHAT is outstanding; these say what the controls to
// fix it should be populated with, and write the fixes.

/** A party as the setup panel's roster shows it. Narrower than `CaseParty`
 *  (#1111's Parties screen type) on purpose — the roster ticks clients, it does
 *  not maintain names, aliases or notes. General party maintenance stays on the
 *  Parties screen rather than being duplicated here. */
export interface SetupParty {
  id: string;
  canonical_name: string;
  short_name: string | null;
  roles: string[];
  /** Whom we represent. NOT a role: two defendants can share every role while
   *  only one is our client, and this is what drives which column is editable
   *  and what the export includes. */
  is_client: boolean;
}

export interface MatterDetails {
  caption: string | null;
  court: string | null;
  docket: string | null;
  judge: string | null;
  record_sources: string | null;
}

export interface SetupPleading {
  id: string;
  kind: 'complaint' | 'cross_complaint';
  label: string | null;
  filed_on: string | null;
  /** Current membership of each side, as party ids. */
  sides: Record<MatrixSide, string[]>;
}

export interface MatrixSetupContext {
  case_ref: string;
  matter_details: MatterDetails;
  parties: SetupParty[];
  /** When somebody last confirmed the client roster, and who. Null ⇒ the
   *  `client_parties_unconfirmed` item is outstanding. */
  /** When somebody confirmed the matter's own details, and who. Null ⇒ the
   *  `matter_details_unconfirmed` item is outstanding. */
  caption_confirmed_at: string | null;
  caption_confirmed_by_email: string | null;
  client_parties_confirmed_at: string | null;
  client_parties_confirmed_by_email: string | null;
  pleadings: SetupPleading[];
}

export async function fetchMatrixSetupContext(
  caseId: string,
  opts?: { signal?: AbortSignal },
): Promise<MatrixSetupContext> {
  return api.get<MatrixSetupContext>(
    `/cases/${caseId}/claims-matrix/setup-context`, { signal: opts?.signal });
}

export interface ClientPartiesResult {
  parties: SetupParty[];
  client_parties_confirmed_at: string | null;
  client_parties_confirmed_by_email: string | null;
}

/**
 * Set the client roster, and optionally confirm it.
 *
 * `partyIds` is the **complete** client set, not a delta — every active party
 * not named is unset. That is deliberate: the backfill's selections are
 * *proposals* (it reads the old `cases.represents_side`, which the previous
 * screen wrote whenever a user switched perspective tabs), so a preselected
 * party can be wrong rather than merely incomplete, and confirming without
 * unticking it would bless the mistake.
 *
 * `confirm` clears `client_parties_unconfirmed`, which is a different fact from
 * `no_client_party`: "a client exists" is not "somebody checked who our clients
 * are", and only the second makes it safe to treat every other party as the
 * other side. Attribution is taken server-side from the authenticated user —
 * there is no email to pass.
 */
export async function saveClientParties(
  caseId: string,
  partyIds: string[],
  confirm: boolean,
): Promise<ClientPartiesResult> {
  return api.put<ClientPartiesResult>(
    `/cases/${caseId}/claims-matrix/client-parties`,
    { party_ids: partyIds, confirm },
  );
}

/** The matter's own details. All five fields are sent every time — the server's
 *  `set_case_caption()` takes them positionally and has no "leave this one
 *  alone", so a partial send blanks what it omits.
 *
 *  Placement is provisional: editing case-level settings on a Claims Matrix
 *  screen is the open question, and nothing else in the product can set these. */
export async function saveMatterDetails(
  caseId: string,
  details: MatterDetails,
  confirm = false,
): Promise<MatterDetails & { caption_confirmed_at: string | null }> {
  return api.patch(`/cases/${caseId}/claims-matrix/matter-details`,
    { ...details, confirm });
}

/** Who is on each side of a pleading. Both sides complete, replacing what is
 *  there. Clears `pleading_missing_side`. */
export async function savePleadingParties(
  caseId: string,
  pleadingId: string,
  sides: Record<MatrixSide, string[]>,
): Promise<{ pleading_id: string } & Record<MatrixSide, string[]>> {
  return api.put(
    `/cases/${caseId}/claims-matrix/pleadings/${pleadingId}/parties`,
    { asserting: sides.asserting, responding: sides.responding },
  );
}

/** The parties that raised an affirmative defence. Plural (plan D6): a defence
 *  is keyed by answering party and a merge can leave more than one. Clears
 *  `defense_without_asserting_party`. */
export async function saveClaimAssertedBy(
  caseId: string,
  claimId: string,
  partyIds: string[],
): Promise<{ claim_id: string; party_ids: string[] }> {
  return api.put(
    `/cases/${caseId}/claims-matrix/claims/${claimId}/asserted-by`,
    { party_ids: partyIds },
  );
}

/** The text a contention currently reads, after an edit (#1097).
 *
 *  Only the fields the row re-renders from. `text_original` is deliberately NOT
 *  here: it is immutable server-side, so the copy the screen already holds from
 *  the matrix read is still correct, and `edited` is derived from the pair
 *  server-side rather than recomputed here. */
export interface MatrixContentionEdit {
  id: string;
  side: MatrixSide;
  seq: number;
  text_md: string;
  edited: boolean;
  /** `'edited'` after a text change to a row somebody had confirmed — the
   *  confirmation is withdrawn rather than moved to the editor (#1097). */
  review_state: MatrixReviewState;
  edited_by_email: string | null;
  edited_at: string | null;
  /** Both NULL after such a change, which is the whole point of sending them: a
   *  caller that does not refetch must not keep rendering an attestation the
   *  write just destroyed — the name or the date. */
  confirmed_by_email: string | null;
  confirmed_at: string | null;
  is_ours: boolean;
}

/** Edit one contention's text, on our client's side only (#1097).
 *
 *  **Revert is this same call** with the row's `text_original` — there is no
 *  second endpoint, because `text_original` cannot change and so the original is
 *  always in the row already on screen.
 *
 *  Refused server-side with 403 on a contention whose origin party is not our
 *  client, whatever the UI rendered. */
export async function patchContentionText(
  caseId: string,
  contentionId: string,
  textMd: string,
): Promise<{ contention: MatrixContentionEdit }> {
  return api.patch(
    `/cases/${caseId}/claims-matrix/contentions/${contentionId}`,
    { text_md: textMd },
  );
}

/** Stand behind a contention as written — no body, no fields (#1097).
 *
 *  The paired half of `patchContentionText`: writing a sentence is an act of
 *  standing behind it, so an edit confirms the row; a lawyer who reads an
 *  extracted contention and agrees with it must be able to say so without
 *  changing a word.
 *
 *  **Not** #1098's verification, which is per *citation* and asks whether the
 *  cited document says what an evidence sentence claims. Different object,
 *  different act, different columns.
 *
 *  Idempotent: a row this user has already confirmed is returned unchanged. */
export async function confirmContention(
  caseId: string,
  contentionId: string,
): Promise<{ contention: MatrixContentionEdit }> {
  return api.post(
    `/cases/${caseId}/claims-matrix/contentions/${contentionId}/confirm`,
    {},
  );
}

export interface ClaimsMatrixPromoteResult {
  promoted: number;
  superseded: number;
  seeds_kept: number;
  allegations_created: number;
  /** L0 lineage computed at promote (mig 090). */
  lineage_rows: number;
  /** Lineage warnings surfaced at promote. `code` discriminates:
   *  - `lineage_predecessor_ambiguous` (thread_kind/candidate_doc_ids) — no guessed root.
   *  - `carried_evidence_regate_failed` (allegation_id/prior_allegation_id) — a carried
   *    allegation's evidence failed the §5 re-gate (now privileged/un-produced); it was NOT
   *    carried forward and needs re-curation. */
  lineage_warnings: Array<{
    code: string;
    detail: string;
    thread_kind?: string;
    candidate_doc_ids?: string[];
    allegation_id?: string;
    prior_allegation_id?: string;
  }>;
}

/**
 * Promote `as_extracted` claims → confirmed (the as_extracted→confirmed writer),
 * SCOPED to one operative complaint (#620). `complaintId` is the operative
 * pleading's `source_doc_id`; only that scope's claims flip. Idempotent,
 * server-attributed. Rejects (400) when the source is not an operative
 * complaint / cross-complaint (#618). See docs/claims-breakout-CONTRACT.md §2.
 */
export async function promoteClaimsMatrix(
  caseId: string,
  complaintId: string,
  opts?: { signal?: AbortSignal },
): Promise<ClaimsMatrixPromoteResult> {
  return api.post<ClaimsMatrixPromoteResult>(
    `/cases/${caseId}/claims-matrix/promote`,
    { complaint: complaintId },
    { signal: opts?.signal },
  );
}

/** Set the represented side (Decision 11) — picks the default perspective tab. */
export async function setClaimsMatrixPerspective(
  caseId: string,
  side: MatrixPerspective,
): Promise<{ represents_side: MatrixPerspective }> {
  return api.patch<{ represents_side: MatrixPerspective }>(
    `/cases/${caseId}/claims-matrix/perspective`,
    { represents_side: side },
  );
}

/** Edit allegation text / revert to the extractor text / set the
 *  reviewed-empty ack. Exactly one operation per call (server-validated). */
export type MatrixAllegationPatch =
  | { text_md: string }
  | { revert: true }
  | { reviewed_empty: boolean };

export async function patchMatrixAllegation(
  allegationId: string,
  patch: MatrixAllegationPatch,
): Promise<{ allegation: MatrixAllegation }> {
  return api.patch<{ allegation: MatrixAllegation }>(
    `/allegations/${allegationId}`,
    patch,
  );
}

/** One source link on the add-evidence payload. Carries the raw id as a gated
 *  INPUT (consumed server-side into a claim_evidence_sources row; never echoed
 *  back by the read — the GET returns only the opaque open_target). */
export interface MatrixEvidenceSourceInput {
  source_kind: 'document' | 'discovery_response';
  document_id?: string;
  discovery_item_id?: string;
  discovery_item_response_id?: string;
  display_label: string;
  page_or_paragraph?: string;
}

export interface MatrixAddEvidenceInput {
  perspective: MatrixPerspective;
  text_md: string;
  sources: MatrixEvidenceSourceInput[];
  /** The save was seeded by an A6 prefill → born metadata_source='ai_extracted'. */
  from_prefill?: boolean;
  /** The statement the prefill returned (born-state diff → 'edited' if changed). */
  prefill_statement?: string;
}

export async function addMatrixEvidence(
  allegationId: string,
  input: MatrixAddEvidenceInput,
): Promise<{ evidence_cell: MatrixEvidenceCell }> {
  return api.post<{ evidence_cell: MatrixEvidenceCell }>(
    `/allegations/${allegationId}/evidence`,
    input,
  );
}

/** Edit text / replace sources / toggle confirm (REVERSIBLE both ways). Exactly
 *  one operation per call (server-validated). Un-confirm preserves the origin. */
export type MatrixEvidenceCellPatch =
  | { text_md: string }
  | { sources: MatrixEvidenceSourceInput[] }
  | { confirm: boolean };

export async function patchMatrixEvidenceCell(
  cellId: string,
  patch: MatrixEvidenceCellPatch,
): Promise<{ evidence_cell: MatrixEvidenceCell }> {
  return api.patch<{ evidence_cell: MatrixEvidenceCell }>(
    `/evidence-cells/${cellId}`,
    patch,
  );
}

export async function deleteMatrixEvidenceCell(
  cellId: string,
): Promise<{ deleted: boolean; id: string }> {
  return api.delete<{ deleted: boolean; id: string }>(
    `/evidence-cells/${cellId}`,
  );
}

/** L1 wording-history timeline — the full lineage chain (every promoted version),
 *  oldest→newest. Returns `[]` when there are fewer than 2 versions. */
export async function fetchAllegationVersions(
  allegationId: string,
): Promise<{ versions: MatrixAllegationVersion[] }> {
  return api.get<{ versions: MatrixAllegationVersion[] }>(
    `/allegations/${allegationId}/versions`,
  );
}

/** A STORED matrix evidence source resolved for the click-to-peek popover — the
 *  same shape as the chronology {@link SourcePreview} plus, for a discovery source,
 *  the in-app navigation target the FE needs to Open the item. The raw document_id /
 *  s3_key are resolved server-side and NEVER returned (the matrix read wire only
 *  ever exposes the opaque open_target.source_link_id). */
export interface MatrixSourcePreview extends SourcePreview {
  /** Discovery in-app nav target — present only for a discovery_response source;
   *  resolved server-side from the link id, returned ONLY on this peek response. */
  nav_discovery_item_id?: string | null;
}

export type MatrixSourcePreviewKey =
  | { evidence_id: string }
  | { source_link_id: string };

export interface MatrixLocatorInput {
  exhibit_label?: string;
  bates?: string;
  para?: string;
  page_start?: number;
  page_end?: number;
  line_start?: number;
  line_end?: number;
}

export interface MatrixCaptureEvidenceBody {
  source_kind: 'document';
  source_id: string;
  evidence_id: string;
  quote?: string;
  support_md?: string;
  locator?: MatrixLocatorInput;
}

export interface MatrixCaptureEvidence {
  id: string;
  position: number;
  source_kind: 'document' | 'discovery_response';
  cite: string;
  quote: string | null;
  support_md: string | null;
  edited: boolean;
  locator: Record<'exhibit_label' | 'bates' | 'para', string | null>
    & Record<'page_start' | 'page_end' | 'line_start' | 'line_end', number | null>;
  objections_only: boolean;
  review_state: MatrixReviewState;
  confirmed_by_email: string | null;
  confirmed_at: string | null;
  verified_state: MatrixEvidence['verified_state'];
}

export interface MatrixSourceLinkLatest {
  evidence_id: string;
  contention_id: string;
  pleading_id: string;
  pleading_title: string | null;
  text_md: string;
  cite: string | null;
  created_at: string;
}

export interface MatrixSourceLinksSummary {
  count: number;
  latest: MatrixSourceLinkLatest | null;
}

/** Resolve a stored matrix evidence source's opaque `source_link_id` → its preview
 *  (label + AI summary + Open descriptor), giving a stored evidence chip the same
 *  click-to-peek the chronology chips have. RLS-scoped server-side; never echoes a
 *  raw document/discovery id back onto the matrix read wire. */
export async function fetchMatrixSourcePreview(
  caseId: string,
  key: MatrixSourcePreviewKey,
): Promise<MatrixSourcePreview> {
  return api.post<MatrixSourcePreview>(
    `/cases/${caseId}/claims-matrix/source-preview`,
    key,
  );
}

export async function createMatrixCaptureEvidence(
  caseId: string,
  contentionId: string,
  body: MatrixCaptureEvidenceBody,
): Promise<{ evidence: MatrixCaptureEvidence }> {
  return api.post<{ evidence: MatrixCaptureEvidence }>(
    `/cases/${caseId}/claims-matrix/contentions/${contentionId}/evidence`,
    body,
  );
}

/** A client party that may raise a new contention on a count (PR #1269).
 *  Advisory: creation re-checks it. */
export interface MatrixEligibleOrigin {
  id: string;
  canonical_name: string;
  short_name: string | null;
  side: MatrixSide;
}

export async function fetchMatrixEligibleOrigins(
  caseId: string,
  pleadingId: string,
  claimId: string,
  opts?: { signal?: AbortSignal },
): Promise<{ parties: MatrixEligibleOrigin[] }> {
  const qs = new URLSearchParams({ pleading_id: pleadingId, claim_id: claimId });
  return api.get<{ parties: MatrixEligibleOrigin[] }>(
    `/cases/${caseId}/claims-matrix/eligible-origins?${qs.toString()}`, { signal: opts?.signal });
}

export interface MatrixCreateContentionBody {
  pleading_id: string;
  claim_id: string;
  text_md: string;
  origin_party_id: string;
}

/** The authoring projection the create endpoint returns — narrower than a
 *  matrix read's `MatrixContention` (no origin_party, evidence, provenance). */
export interface MatrixCreatedContention {
  id: string;
  side: MatrixSide;
  seq: number;
  text_md: string;
  edited: boolean;
  review_state: MatrixReviewState;
  edited_by_email: string | null;
  edited_at: string | null;
  confirmed_by_email: string | null;
  confirmed_at: string | null;
  is_ours: boolean;
}

/** Not idempotent (PR #1269): after an uncertain failure, reconcile before creating again. */
export async function createMatrixContention(
  caseId: string,
  body: MatrixCreateContentionBody,
): Promise<{ contention: MatrixCreatedContention }> {
  return api.post<{ contention: MatrixCreatedContention }>(
    `/cases/${caseId}/claims-matrix/contentions`,
    body,
  );
}

export type MatrixReconcileAction = 'confirm_fits' | 'revise' | 'archive';

/** L2 reconcile an amended allegation (admin-gated): confirm_fits carries the prior
 *  evidence forward, revise marks it for a corrective edit, archive acks a withdrawal. */
export async function reconcileAllegation(
  caseId: string,
  allegationId: string,
  action: MatrixReconcileAction,
): Promise<{ reconcile_status: MatrixReconcileStatus; action: MatrixReconcileAction }> {
  return api.post<{ reconcile_status: MatrixReconcileStatus; action: MatrixReconcileAction }>(
    `/cases/${caseId}/claims-matrix/reconcile`,
    { allegation_id: allegationId, action },
  );
}

/** A matrix source-picker hit. Restricted to {document, discovery_response}
 *  (no pleading/deposition) and DISCLOSURE/PRIVILEGE-gated server-side. Shape
 *  mirrors the chronology candidate so SourcePicker can be reused. */
export interface MatrixSourceCandidate {
  source_kind: 'document' | 'discovery_response';
  source_id: string;
  discovery_item_response_id: string | null;
  display_label: string;
  secondary: string | null;
  date_hint: string | null;
}

export async function fetchMatrixSourceLinks(
  caseId: string,
  sourceKind: MatrixSourceCandidate['source_kind'],
  sourceId: string,
): Promise<MatrixSourceLinksSummary> {
  const params = new URLSearchParams({ source_kind: sourceKind, source_id: sourceId });
  return api.get<MatrixSourceLinksSummary>(
    `/cases/${caseId}/claims-matrix/source-links?${params.toString()}`,
  );
}

export interface MatrixSourceSearchResponse {
  results: MatrixSourceCandidate[];
  query: string;
  kind: 'all' | MatrixSourceCandidate['source_kind'];
  source_kinds: MatrixSourceCandidate['source_kind'][];
}

export async function searchMatrixSources(
  caseId: string,
  q: string,
  opts: { kind?: 'all' | MatrixSourceCandidate['source_kind']; limit?: number } = {},
): Promise<MatrixSourceSearchResponse> {
  const params = new URLSearchParams({ q });
  if (opts.kind && opts.kind !== 'all') params.set('kind', opts.kind);
  if (opts.limit) params.set('limit', String(opts.limit));
  return api.get<MatrixSourceSearchResponse>(
    `/cases/${caseId}/claims-matrix/sources/search?${params.toString()}`,
  );
}

/** Map a matrix candidate to the add-evidence source payload (always resolved —
 *  the picker only surfaces real corpus rows). */
export function matrixCandidateToSourceInput(
  c: MatrixSourceCandidate,
): MatrixEvidenceSourceInput {
  const base: MatrixEvidenceSourceInput = {
    source_kind: c.source_kind,
    display_label: c.display_label,
  };
  if (c.source_kind === 'document') base.document_id = c.source_id;
  else {
    base.discovery_item_id = c.source_id;
    if (c.discovery_item_response_id)
      base.discovery_item_response_id = c.discovery_item_response_id;
  }
  return base;
}

/** Map a matrix candidate to a {@link ChronologySource} so the reusable
 *  SourcePeek / SourcePreview machinery can preview a CHOSEN-but-not-yet-cited
 *  source in the add-evidence modal. The matrix candidate's two kinds map onto
 *  the chronology source kinds 1:1. `id` is a non-persisted placeholder. */
export function matrixCandidateToSource(c: MatrixSourceCandidate): ChronologySource {
  return {
    id: `matrix-candidate:${c.source_kind}:${c.source_id}`,
    source_kind: c.source_kind,
    document_id: c.source_kind === 'document' ? c.source_id : null,
    source_doc_id: null,
    discovery_item_id: c.source_kind === 'discovery_response' ? c.source_id : null,
    discovery_item_response_id: c.discovery_item_response_id,
    is_resolved: true,
    display_label: c.display_label,
    paragraph_or_page: null,
  };
}

/** The add-evidence AI prefill (Shape A) for one source, TARGETED at an
 *  allegation. Returns the draft statement only (no persist). */
export interface MatrixPrefillResult {
  statement: string;
  model: string;
}

export async function prefillMatrixEvidence(
  caseId: string,
  input: {
    source_kind: 'document' | 'discovery_response';
    source_id: string;
    allegation_id: string;
    discovery_item_response_id?: string | null;
  },
): Promise<MatrixPrefillResult> {
  return api.post<MatrixPrefillResult>(
    `/cases/${caseId}/claims-matrix/prefill`,
    input,
  );
}

// ─── Export jobs (#763) ─────────────────────────────────────────────────────
// The ZIP pipeline. Distinct from the Table export, which is a synchronous
// spreadsheet response — these queue a background job and return a row to poll.
// `table` is that same spreadsheet built as a job, for a set the synchronous
// response cannot carry (#767).

export type ExportPackage =
  | 'native' | 'review_package' | 'combined_pdf' | 'production' | 'table';

/**
 * `ready_with_exceptions` is NOT a flavour of failed: the artifact exists and
 * is downloadable, and `docs_failed > 0`. `failed` has nothing to download.
 * The UI must not collapse them.
 */
export type ExportJobStatus =
  | 'queued'
  | 'building'
  | 'ready'
  | 'ready_with_exceptions'
  | 'failed'
  | 'cancelled'
  | 'expired';

export interface ExportJob {
  id: string;
  package: ExportPackage;
  status: ExportJobStatus;
  docs_total: number;
  docs_succeeded: number;
  docs_failed: number;
  artifact_bytes: number | null;
  expires_at: string | null;
  requested_by_email: string;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  cancel_requested: boolean;
  error_class: string | null;
  error_msg: string | null;
  /** The name the file downloads as — the same one the completion email gives. */
  filename?: string;
}

/**
 * What preflight returns — and it has a side effect: it PERSISTS the resolved
 * document set as a draft job. Confirming queues that exact set.
 *
 * So this must not be called when the export modal opens. Re-resolving the
 * filter at build time would make the estimate a lie (reviewers keep working
 * while a job waits), which is why the snapshot exists; the cost is that
 * merely opening the modal must not mint one. `fetchFilteredDocsCounts` stays
 * the cheap label for that.
 */
export interface ExportPreflight {
  job_id: string;
  status: 'draft';
  package?: ExportPackage;
  total_families: number;
  total_documents: number;
  printable: number;
  native_only: number;
  estimated_bytes: number;
  /** From each document's Bates range. Exact for the Review package (the
   *  producer numbered every page) and for Original files unless a range
   *  fails to parse — see `estimated_pages_exact`. */
  estimated_pages: number;
  /** True when `estimated_pages` is an exact count rather than a floor. */
  estimated_pages_exact?: boolean;
  /** Combined PDF only (#765): about how many volumes it splits into. It can
   *  only go up — the worker never splits a family, so one straddling a
   *  boundary pushes a volume over the ceiling. */
  estimated_volumes?: number;
  /** False when a shipped document has no recorded size, or ships as a stitched
   *  PDF whose recorded size belongs to the undelivered native: a floor. */
  estimated_bytes_exact?: boolean;
  /** Of `printable`, how many ship as a PDF of the produced page images rather
   *  than a vendor native (#964). */
  stitched?: number;
  draft_ttl: string;
  /** `pages` and `bytes` are null for the Table package, which is capped on
   *  documents alone. */
  limits: { documents: number; pages: number | null; bytes: number | null };
}

export async function preflightExport(
  caseId: string,
  params: DocumentSearchParams & { package?: ExportPackage },
): Promise<ExportPreflight> {
  return api.post<ExportPreflight>(`/cases/${caseId}/exports/preflight`, params);
}

/**
 * Queues the draft. `already_confirmed` comes back when the job was already
 * queued (or further along) — a double-click, not a stale estimate — so the
 * status is whatever it had reached, not always 'queued'.
 */
export async function confirmExport(
  caseId: string,
  jobId: string,
): Promise<{ job_id: string; status: ExportJobStatus; already_confirmed?: boolean }> {
  return api.post(`/cases/${caseId}/exports/${jobId}/confirm`);
}

export async function fetchExports(caseId: string): Promise<{ exports: ExportJob[] }> {
  return api.get<{ exports: ExportJob[] }>(`/cases/${caseId}/exports`);
}

export async function fetchExport(caseId: string, jobId: string): Promise<ExportJob> {
  return api.get<ExportJob>(`/cases/${caseId}/exports/${jobId}`);
}

/**
 * Requests cancellation. The worker owns the move to `cancelled`, so what
 * comes back is the flag plus the status the job still has — not a full job row.
 */
export async function cancelExport(
  caseId: string,
  jobId: string,
): Promise<{ job_id: string; status: ExportJobStatus; cancel_requested: boolean }> {
  return api.post(`/cases/${caseId}/exports/${jobId}/cancel`);
}

/**
 * Authorises against the job row, then returns a FRESH short-lived presigned
 * URL. Never stored or shared: it is minted per click and expires in minutes.
 */
export async function fetchExportDownloadUrl(
  caseId: string,
  jobId: string,
): Promise<{ url: string; expires_in: number }> {
  return api.get<{ url: string; expires_in: number }>(
    `/cases/${caseId}/exports/${jobId}/download`,
  );
}
